import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  TrendingUp, Heart, UserPlus, UserMinus, Eye, MessageCircle, Send, Star, Scan,
  Clock, RefreshCw, Calendar, Instagram, BarChart3, Target, Activity, Zap,
  ChevronDown, ChevronRight, Search, Users, BookOpen,
  MapPin, Globe, Plus, Trash2, Lock, Tag, X, Check, Hash, User as UserIcon, FileText,
  List, AlertCircle
} from 'lucide-react';
import useStickyPin from '../hooks/useStickyPin';
import axios from 'axios';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const ACTION_TYPE_META = {
  'LikeAction': { label: 'Liked post from', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  'FollowAction': { label: 'Followed', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  'GetProfileAction': { label: 'Viewed profile', icon: Users, color: '#a591ef', bg: 'rgba(165,145,239,.12)' },
  'StoryWatchAction': { label: 'Viewed story from', icon: BookOpen, color: '#f59e0b', bg: 'rgba(245,158,11,.12)' },
  'StoryLikeAction': { label: 'Liked story from', icon: Star, color: '#ec4899', bg: 'rgba(236,72,153,.12)' },
  'UnfollowAction': { label: 'Unfollowed', icon: UserMinus, color: '#f97316', bg: 'rgba(249,115,22,.12)' },
  'CommentAction': { label: 'Commented on', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.12)' },
  'DirectMessageAction': { label: 'DM to', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.12)' },
  'ScrapeAction': { label: 'Scraped', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.12)' },
};

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`
  width: 100%;
`;

const TopBar = styled.div`
  display: flex; align-items: center; gap: 14px; margin-bottom: 20px; flex-wrap: wrap;
  background: var(--background);

  @media (max-width: 768px) {
    position: sticky;
    top: 0;
    z-index: 50;
    gap: 8px;
    margin: 0 -12px 16px;
    padding: 10px 12px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  }
`;

const Title = styled.h1`
  font-size: 22px; font-weight: 800; color: var(--text-primary);
  display: flex; align-items: center; gap: 10px; margin: 0;
  svg { color: var(--primary); }
`;

const Spacer = styled.div` flex: 1; `;

const AccountPicker = styled.div`
  position: relative;
`;

const PickerBtn = styled.button`
  display: flex; align-items: center; gap: 8px; padding: 8px 14px;
  border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg);
  color: var(--text-primary); font-size: 13px; font-weight: 600; cursor: pointer;
  transition: all .15s; display: flex; align-items: center; gap: 6px;
  &:hover { border-color: var(--primary); }
  img { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
`;

const PickerDrop = styled.div`
  position: absolute; top: 100%; left: 0; margin-top: 4px;
  background: var(--card-bg); border: 1px solid var(--border);
  border-radius: 10px; min-width: 240px; z-index: 100;
  box-shadow: 0 8px 24px rgba(0,0,0,.12); max-height: 300px; overflow-y: auto;
`;

const PickerItem = styled.button`
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 8px 14px; border: none; background: ${p => p.$sel ? 'rgba(165,145,239,.1)' : 'transparent'};
  color: var(--text-primary); font-size: 12px; cursor: pointer;
  &:hover { background: var(--surface-hover); }
  img { width: 20px; height: 20px; border-radius: 50%; }
`;

const DateInput = styled.input`
  padding: 8px 12px; border-radius: 8px; border: 1px solid var(--border);
  background: var(--card-bg); color: var(--text-primary); font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
  &:focus { border-color: var(--primary); outline: none; }
`;

const SmallBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 8px 14px;
  border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg);
  color: var(--text-secondary); font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all .15s;
  &:hover { border-color: var(--primary); color: var(--primary); }
  &:disabled { opacity: .5; }
`;

/* ── Tabs ── */
const TabBar = styled.div`
  display: flex; gap: 2px; margin-bottom: 20px;
  border-bottom: 1px solid var(--border); padding-bottom: 0;
`;

const Tab = styled.button`
  padding: 10px 18px; border: none; background: none;
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-muted)'};
  font-size: 13px; font-weight: ${p => p.$active ? '700' : '500'};
  cursor: pointer; border-bottom: 2px solid ${p => p.$active ? 'var(--primary)' : 'transparent'};
  transition: all .15s; display: flex; align-items: center; gap: 6px;
  &:hover { color: var(--primary); }
`;

/* ── Stats Cards ── */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px; margin-bottom: 20px;
`;

const StatCard = styled.div`
  padding: 16px; border-radius: 12px;
  background: ${p => p.$active ? 'rgba(165,145,239,.08)' : 'var(--card-bg)'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--card-border)'};
  transition: all .2s; cursor: pointer; user-select: none;
  &:hover { border-color: rgba(165,145,239,.5); transform: translateY(-1px); }
`;

const DetailTable = styled.div`
  border-radius: 12px; background: var(--card-bg); border: 1px solid var(--card-border);
  overflow: hidden; margin-bottom: 20px;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const DetailTableHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-bottom: 1px solid var(--border); background: var(--surface);
  span { font-size: 12px; font-weight: 700; color: var(--text-primary); display: flex; align-items: center; gap: 6px; }
`;

const DetailTableHead = styled.div`
  display: grid; grid-template-columns: 150px 140px 1fr 1fr 1.2fr;
  padding: 8px 16px; font-size: 10px; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .4px; border-bottom: 1px solid var(--border);
  background: var(--surface);

  @media (max-width: 768px) {
    min-width: 680px;
  }
`;

const DetailTableRow = styled.div`
  display: grid; grid-template-columns: 150px 140px 1fr 1fr 1.2fr;
  padding: 8px 16px; align-items: center; border-bottom: 1px solid var(--border);
  font-size: 12px; transition: background .1s;
  &:hover { background: var(--surface-hover); }
  &:last-child { border-bottom: none; }

  @media (max-width: 768px) {
    min-width: 680px;
  }
`;

const DetailTableScroll = styled.div`
  max-height: none; overflow-y: visible;
`;

const FilterRow = styled.div`
  display: grid; grid-template-columns: 150px 140px 1fr 1fr 1.2fr;
  padding: 6px 16px; gap: 4px; border-bottom: 1px solid var(--border);
  background: var(--surface);
  @media (max-width: 768px) { min-width: 680px; }
  select, input {
    width: 100%; padding: 4px 6px; border-radius: 4px; border: 1px solid var(--card-border);
    background: var(--card-bg); color: var(--text-primary); font-size: 10px;
    &:focus { outline: none; border-color: var(--primary); }
  }
`;

const SortHeader = styled.div`
  cursor: pointer; display: flex; align-items: center; gap: 3px; user-select: none;
  &:hover { color: var(--primary); }
`;

const PaginationBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; border-top: 1px solid var(--border); background: var(--surface);
  font-size: 11px; color: var(--text-muted);
`;

const PaginationBtns = styled.div`
  display: flex; gap: 4px;
  button {
    padding: 4px 10px; border-radius: 4px; border: 1px solid var(--card-border);
    background: var(--card-bg); color: var(--text-secondary); font-size: 11px;
    font-weight: 600; cursor: pointer; transition: all .15s;
    &:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); }
    &:disabled { opacity: .4; cursor: default; }
    &[data-active='true'] { background: var(--primary); color: #fff; border-color: var(--primary); }
  }
`;

const ProfileBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px; padding: 1px 6px;
  border-radius: 3px; font-size: 9px; font-weight: 600;
  background: ${p => p.$bg || 'var(--surface)'}; color: ${p => p.$color || 'var(--text-muted)'};
  white-space: nowrap;
`;

const ActionTypeBadge = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700;
  background: ${p => p.$bg || 'var(--surface)'}; color: ${p => p.$color || 'var(--text-muted)'};
`;

const StatLabel = styled.div`
  font-size: 10px; font-weight: 600; text-transform: uppercase;
  letter-spacing: .5px; color: var(--text-muted);
  display: flex; align-items: center; gap: 6px; margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 24px; font-weight: 800; color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
`;

const StatSub = styled.div`
  font-size: 10px; color: var(--text-muted); margin-top: 2px;
`;

/* ── Account Profile Card ── */
const ProfileCard = styled.div`
  display: flex; align-items: center; gap: 16px; padding: 16px 20px;
  border-radius: 12px; background: var(--card-bg); border: 1px solid var(--card-border);
  margin-bottom: 20px;
`;

const ProfileAvatar = styled.div`
  width: 52px; height: 52px; border-radius: 50%; background: var(--gradient);
  display: flex; align-items: center; justify-content: center; color: #fff;
  font-size: 18px; font-weight: 700; flex-shrink: 0; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const ProfileInfo = styled.div`
  flex: 1;
  h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 2px; }
  span { font-size: 11px; color: var(--text-muted); }
`;

const ProfileStat = styled.div`
  text-align: center; padding: 0 14px;
  div:first-child { font-size: 16px; font-weight: 700; color: var(--text-primary); }
  div:last-child { font-size: 9px; color: var(--text-muted); text-transform: uppercase; }
`;

const EmptyState = styled.div`
  text-align: center; padding: 40px; color: var(--text-muted);
  svg { opacity: .2; margin-bottom: 8px; }
  p { font-size: 13px; }
`;

/* ── Cibles Table Layout (screenshot 3 style) ── */
const CiblesStatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px; margin-bottom: 16px;
`;

const CiblesStatCard = styled.div`
  padding: 14px 16px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border);
  .cs-label { font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .5px; color: var(--text-muted); display: flex; align-items: center; gap: 5px; margin-bottom: 4px; }
  .cs-value { font-size: 22px; font-weight: 800; color: ${p => p.$color || 'var(--text-primary)'};
    font-family: 'JetBrains Mono', monospace; }
`;

const CiblesTable = styled.table`
  width: 100%; border-collapse: separate; border-spacing: 0;
  border-radius: 12px; overflow: hidden;
  background: var(--card-bg); border: 1px solid var(--card-border);
  th { padding: 8px 12px; text-align: left; font-size: 10px; font-weight: 700;
    color: var(--text-muted); text-transform: uppercase; letter-spacing: .4px;
    background: var(--surface); border-bottom: 2px solid var(--border);
    white-space: nowrap; cursor: pointer; user-select: none;
    &:hover { color: var(--primary); } }
  td { padding: 10px 12px; font-size: 12px; color: var(--text-secondary);
    border-bottom: 1px solid var(--border); vertical-align: middle; }
  tr:hover td { background: var(--surface-hover); }
`;

const TargetStatusDot = styled.button`
  width: 14px; height: 14px; border-radius: 50%; display: inline-block;
  background: ${p => p.$active ? 'var(--success)' : 'var(--text-muted)'};
  border: 2px solid ${p => p.$active ? 'rgba(34,197,94,.4)' : 'rgba(150,150,150,.3)'};
  cursor: pointer; transition: all .2s; padding: 0;
  box-shadow: ${p => p.$active ? '0 0 6px rgba(34,197,94,.4)' : 'none'};
  &:hover { transform: scale(1.3); box-shadow: ${p => p.$active ? '0 0 10px rgba(34,197,94,.5)' : '0 0 8px rgba(239,68,68,.4)'};
    background: ${p => p.$active ? 'var(--error)' : 'var(--success)'};
    border-color: ${p => p.$active ? 'rgba(239,68,68,.4)' : 'rgba(34,197,94,.4)'}; }
`;

const WeightInput = styled.input`
  width: 50px; padding: 3px 6px; border-radius: 4px; font-size: 12px;
  font-weight: 700; font-family: 'JetBrains Mono', monospace;
  background: var(--surface); border: 1px solid var(--card-border);
  color: var(--text-primary); text-align: center; outline: none;
  transition: border-color .15s;
  &:focus { border-color: var(--primary); }
  &::-webkit-inner-spin-button { opacity: 0; }
  &:hover::-webkit-inner-spin-button { opacity: 1; }
`;

const CiblesFilterRow = styled.div`
  display: flex; gap: 8px; align-items: center; margin-bottom: 12px; flex-wrap: wrap;
`;

const CiblesPagination = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 0; font-size: 11px; color: var(--text-muted);
  button { padding: 5px 12px; border-radius: 6px; border: 1px solid var(--border);
    background: var(--card-bg); color: var(--text-secondary); font-size: 11px; font-weight: 600; cursor: pointer;
    &:disabled { opacity: .3; cursor: not-allowed; }
    &:hover:not(:disabled) { border-color: var(--primary); } }
  .pg-nums { display: flex; gap: 4px; }
  .pg-active { background: var(--primary); color: #fff; border-color: var(--primary); }
`;

const FilterSel = styled.select`
  padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-primary); font-size: 11px;
  &:focus { border-color: var(--primary); outline: none; }
`;

const FilterInputSmall = styled.input`
  padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border);
  background: var(--surface); color: var(--text-primary); font-size: 11px;
  width: 70px;
  &:focus { border-color: var(--primary); outline: none; }
  &::placeholder { color: var(--text-muted); }
`;

const RemoveBtn = styled.button`
  width: 18px; height: 18px; border-radius: 4px; border: none;
  background: rgba(239,68,68,.1); color: #ef4444; cursor: pointer;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &:hover { background: rgba(239,68,68,.2); }
`;

/* ── Add Target Modal ── */
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.6); backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 2000;
`;
const ModalBox = styled.div`
  background: var(--surface); border-radius: 16px;
  width: 440px; max-width: 90vw; box-shadow: 0 20px 60px rgba(0,0,0,.4);
  border: 1px solid var(--border); overflow: hidden;
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 20px 24px; border-bottom: 1px solid var(--border);
`;
const ModalTitle = styled.h3`
  margin: 0; font-size: 18px; font-weight: 800; color: var(--text-primary);
  display: flex; align-items: center; gap: 10px;
`;
const ModalCloseBtn = styled.button`
  background: none; border: none; cursor: pointer; color: var(--text-muted);
  padding: 4px; border-radius: 6px; display: flex; transition: all .15s;
  &:hover { color: var(--error); background: rgba(239,68,68,.1); }
`;
const ModalBody = styled.div`
  padding: 24px; display: flex; flex-direction: column; gap: 20px;
`;
const ModalField = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;
const ModalLabel = styled.label`
  font-size: 12px; font-weight: 700; color: var(--text-secondary);
  text-transform: uppercase; letter-spacing: 0.5px;
`;
const ModalInput = styled.input`
  padding: 12px 16px; background: var(--bg); border: 1px solid var(--border);
  border-radius: 10px; color: var(--text-primary); font-size: 14px; font-weight: 600;
  transition: all .2s;
  &::placeholder { color: var(--text-muted); }
  &:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px rgba(88,101,242,.15); }
`;
const ModalTypeGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
`;
const ModalTypeBtn = styled.button`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 16px 12px; border-radius: 12px; cursor: pointer;
  border: 2px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  background: ${p => p.$active ? 'rgba(88,101,242,.12)' : 'var(--bg)'};
  color: ${p => p.$active ? 'var(--primary)' : 'var(--text-secondary)'};
  font-size: 13px; font-weight: 700; transition: all .2s;
  &:hover { border-color: var(--primary); background: rgba(88,101,242,.06); }
`;
const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px; border-top: 1px solid var(--border);
`;
const ModalCancelBtn = styled.button`
  padding: 10px 20px; border-radius: 10px; border: 1px solid var(--border);
  background: transparent; color: var(--text-secondary); font-weight: 700;
  font-size: 14px; cursor: pointer; transition: all .2s;
  &:hover { background: var(--bg); color: var(--text-primary); }
`;
const ModalSubmitBtn = styled.button`
  padding: 10px 24px; border-radius: 10px; border: none;
  background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
  color: white; font-weight: 700; font-size: 14px; cursor: pointer;
  transition: all .2s; display: flex; align-items: center; gap: 8px;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(88,101,242,.4); }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; box-shadow: none; }
`;
const AddTargetTopBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white; font-size: 12px; font-weight: 700; cursor: pointer;
  transition: all .2s; white-space: nowrap;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(34,197,94,.3); }
  &:active { transform: translateY(0); }
`;
const ModalAcctInfo = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; background: var(--bg); border-radius: 10px;
  border: 1px solid var(--border);
  img { width: 32px; height: 32px; border-radius: 50%; border: 2px solid var(--primary); object-fit: cover; }
  span { font-size: 14px; font-weight: 700; color: var(--primary); }
`;

/* ── Split Layout (merged overview + chart) ── */
const SplitPanel = styled.div`
  flex: ${p => p.$flex || 1};
  min-width: 0;
  display: flex; flex-direction: column; gap: 12px;
  transition: all .3s ease;
  ${p => p.$collapsed && `flex: 0 0 40px; overflow: hidden;`}
`;

const MetricCard = styled.div`
  padding: 12px 14px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border);
  .mc-label { font-size: 9px; font-weight: 700; text-transform: uppercase;
    letter-spacing: .4px; color: var(--text-muted); display: flex; align-items: center; gap: 5px; margin-bottom: 4px; }
  .mc-value { font-size: 20px; font-weight: 800; color: ${p => p.$color || 'var(--text-primary)'};
    font-family: 'JetBrains Mono', monospace; }
  .mc-sub { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
`;

const MetricsGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
`;

/* ═══════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════ */

const ACTION_MAP = {
  'LikeAction': { label: 'Liked post from', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.1)' },
  'FollowAction': { label: 'Followed', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.1)' },
  'UnfollowAction': { label: 'Unfollowed', icon: UserMinus, color: '#ef4444', bg: 'rgba(239,68,68,.1)' },
  'GetProfileAction': { label: 'Viewed profile', icon: Eye, color: '#a591ef', bg: 'rgba(165,145,239,.1)' },
  'StoryWatchAction': { label: 'Viewed story from', icon: Eye, color: '#f59e0b', bg: 'rgba(245,158,11,.1)' },
  'StoryLikeAction': { label: 'Liked story from', icon: Heart, color: '#ec4899', bg: 'rgba(236,72,153,.1)' },
  'CommentAction': { label: 'Commented on', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.1)' },
  'DirectMessageAction': { label: 'DM to', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.1)' },
  'ScrapeAction': { label: 'Scraped', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.1)' },
};

const today = () => new Date().toISOString().split('T')[0];

/* ═══════════════════════════════════════════════
   SESSIONS TAB CONTENT (sub-component)
   ═══════════════════════════════════════════════ */

const SessionCard = styled.div`
  padding: 12px 16px; border-radius: 10px;
  background: ${p => p.$sel ? 'rgba(165,145,239,.08)' : 'var(--card-bg)'};
  border: 1px solid ${p => p.$sel ? 'var(--primary)' : 'var(--card-border)'};
  cursor: pointer; transition: all .15s; display: flex; align-items: center; gap: 12px;
  &:hover { border-color: var(--primary); transform: translateY(-1px); }
`;

const SessionBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 700;
  background: ${p => p.$bg || 'var(--surface)'}; color: ${p => p.$color || 'var(--text-muted)'};
`;

const SessionDetailGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 8px; margin-top: 12px;
`;

const SessionDetailCard = styled.div`
  padding: 10px 12px; border-radius: 8px;
  background: var(--card-bg); border: 1px solid var(--card-border);
  text-align: center;
  .sd-val { font-size: 18px; font-weight: 800; font-family: 'JetBrains Mono', monospace; color: ${p => p.$color || 'var(--text-primary)'}; }
  .sd-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; color: var(--text-muted); margin-top: 2px; }
`;

function SessionsTabContent({ selAcct, sessionsDate, setSessionsDate, sessionsList, setSessionsList, sessionsLoading, setSessionsLoading, selectedSessionDetail, setSelectedSessionDetail, sessionActions, setSessionActions, sessionActionsLoading, setSessionActionsLoading }) {
  useEffect(() => {
    const fetchSessions = async () => {
      setSessionsLoading(true);
      try {
        const url = selAcct
          ? `/api/sessions?date=${sessionsDate}&account_id=${selAcct}`
          : `/api/sessions?date=${sessionsDate}`;
        const r = await axios.get(url);
        setSessionsList(r.data.sessions || []);
      } catch (e) {
        setSessionsList([]);
      }
      setSessionsLoading(false);
    };
    fetchSessions();
  }, [selAcct, sessionsDate, setSessionsList, setSessionsLoading]);

  const handleSessionClick = async (session) => {
    setSelectedSessionDetail(session);
    setSessionActionsLoading(true);
    try {
      const r = await axios.get(`/api/session-actions/${session.id}`);
      setSessionActions(r.data.actions || []);
    } catch (e) {
      setSessionActions([]);
    }
    setSessionActionsLoading(false);
  };

  const fmtDur = (sec) => {
    if (!sec) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    return h > 0 ? `${h}h${m.toString().padStart(2,'0')}` : `${m}min`;
  };
  const fmtTs = (ts) => {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
  };
  const changeDate = (days) => {
    const d = new Date(sessionsDate);
    d.setDate(d.getDate() + days);
    setSessionsDate(d.toISOString().split('T')[0]);
  };

  const totalInteractions = sessionsList.reduce((s, x) => s + (x.total_interactions || 0), 0);
  const totalLikes = sessionsList.reduce((s, x) => s + (x.likes || 0), 0);
  const totalFollows = sessionsList.reduce((s, x) => s + (x.follows || 0), 0);
  const totalDuration = sessionsList.reduce((s, x) => s + (x.duration || 0), 0);

  return (
    <div>
      {/* Date picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <SmallBtn onClick={() => changeDate(-1)}><ChevronDown size={14} style={{ transform: 'rotate(90deg)' }} /></SmallBtn>
        <DateInput type="date" value={sessionsDate} onChange={e => setSessionsDate(e.target.value)} />
        <SmallBtn onClick={() => changeDate(1)}><ChevronDown size={14} style={{ transform: 'rotate(-90deg)' }} /></SmallBtn>
        <SmallBtn onClick={() => setSessionsDate(today())}><Calendar size={12} /> Aujourd'hui</SmallBtn>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
          {sessionsList.length} session{sessionsList.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Summary stats */}
      <StatsRow>
        <StatCard><div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}><Activity size={11} /> Sessions</div><div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{sessionsList.length}</div></StatCard>
        <StatCard><div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}><TrendingUp size={11} /> Interactions</div><div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{totalInteractions}</div></StatCard>
        <StatCard><div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}><Heart size={11} /> Likes</div><div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: '#ef4444' }}>{totalLikes}</div></StatCard>
        <StatCard><div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}><UserPlus size={11} /> Follows</div><div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: '#22c55e' }}>{totalFollows}</div></StatCard>
        <StatCard><div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}><Clock size={11} /> Durée totale</div><div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{fmtDur(totalDuration)}</div></StatCard>
      </StatsRow>

      {sessionsLoading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Chargement...</div>
      ) : sessionsList.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          <Clock size={32} style={{ opacity: .3, marginBottom: 8 }} /><br/>
          Aucune session pour cette date
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sessionsList.map(s => (
            <SessionCard key={s.id} $sel={selectedSessionDetail?.id === s.id} onClick={() => handleSessionClick(s)}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--surface)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {s.profile_name && s.profile_name !== 'Unknown' ? (
                  <img src={getLocalProfilePicUrl(s.profile_name)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getProfilePicUrl(s.profile_name);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${s.profile_name}`;return;} e.target.style.display='none'; }} />
                ) : <Instagram size={14} style={{ color: 'var(--text-muted)' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>@{s.profile_name || 'Unknown'}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                  <span>{fmtTs(s.start_time)} → {fmtTs(s.end_time)}</span>
                  <span>{fmtDur(s.duration)}</span>
                  {s.session_type && s.session_type !== 'unknown' && <SessionBadge $bg="rgba(165,145,239,.1)" $color="var(--primary)">{s.session_type}</SessionBadge>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                {s.likes > 0 && <SessionBadge $bg="rgba(239,68,68,.1)" $color="#ef4444"><Heart size={9} /> {s.likes}</SessionBadge>}
                {s.follows > 0 && <SessionBadge $bg="rgba(34,197,94,.1)" $color="#22c55e"><UserPlus size={9} /> {s.follows}</SessionBadge>}
                {s.unfollows > 0 && <SessionBadge $bg="rgba(245,158,11,.1)" $color="#f59e0b"><UserMinus size={9} /> {s.unfollows}</SessionBadge>}
                {s.total_interactions > 0 && <SessionBadge $bg="rgba(165,145,239,.1)" $color="var(--primary)"><Zap size={9} /> {s.total_interactions}</SessionBadge>}
              </div>
            </SessionCard>
          ))}
        </div>
      )}

      {/* Session detail panel */}
      {selectedSessionDetail && (
        <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Instagram size={16} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, fontSize: 14 }}>@{selectedSessionDetail.profile_name}</span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {fmtTs(selectedSessionDetail.start_time)} → {fmtTs(selectedSessionDetail.end_time)} · {fmtDur(selectedSessionDetail.duration)}
            </span>
            <button onClick={() => setSelectedSessionDetail(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={14} /></button>
          </div>
          <SessionDetailGrid>
            <SessionDetailCard $color="#ef4444"><div className="sd-val">{selectedSessionDetail.likes || 0}</div><div className="sd-label">Likes</div></SessionDetailCard>
            <SessionDetailCard $color="#22c55e"><div className="sd-val">{selectedSessionDetail.follows || 0}</div><div className="sd-label">Follows</div></SessionDetailCard>
            <SessionDetailCard $color="#f59e0b"><div className="sd-val">{selectedSessionDetail.unfollows || 0}</div><div className="sd-label">Unfollows</div></SessionDetailCard>
            <SessionDetailCard $color="#a591ef"><div className="sd-val">{selectedSessionDetail.get_profiles || 0}</div><div className="sd-label">Profils vus</div></SessionDetailCard>
            <SessionDetailCard $color="#ec4899"><div className="sd-val">{selectedSessionDetail.story_views || 0}</div><div className="sd-label">Stories vues</div></SessionDetailCard>
            <SessionDetailCard $color="#3b82f6"><div className="sd-val">{selectedSessionDetail.comments || 0}</div><div className="sd-label">Commentaires</div></SessionDetailCard>
            <SessionDetailCard $color="#8b5cf6"><div className="sd-val">{selectedSessionDetail.dms || 0}</div><div className="sd-label">DMs</div></SessionDetailCard>
            <SessionDetailCard><div className="sd-val">{selectedSessionDetail.total_private_profiles || 0}</div><div className="sd-label">Privés</div></SessionDetailCard>
          </SessionDetailGrid>

          {/* Session actions list */}
          {sessionActionsLoading ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Chargement des actions...</div>
          ) : sessionActions.length > 0 && (
            <div style={{ marginTop: 12, maxHeight: 300, overflowY: 'auto', borderRadius: 8, border: '1px solid var(--border)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: 'var(--surface)', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Heure</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Action</th>
                    <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Utilisateur</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionActions.slice(0, 200).map((a, i) => {
                    const meta = ACTION_TYPE_META[a.action_type] || { label: a.action_type, icon: Activity, color: 'var(--text-muted)', bg: 'var(--surface)' };
                    const ActionIcon = meta.icon;
                    return (
                      <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '4px 10px', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
                          {a.timestamp ? new Date(a.timestamp * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' }) : '—'}
                        </td>
                        <td style={{ padding: '4px 10px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: meta.bg, color: meta.color }}>
                            <ActionIcon size={9} /> {meta.label}
                          </span>
                        </td>
                        <td style={{ padding: '4px 10px', fontWeight: 600, color: 'var(--primary)' }}>@{a.target_user || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const StatisticsNew = ({ selectedAccountId, defaultTab, onSwitchAccount }) => {
  const [tab, setTab] = useState(defaultTab || 'overview');
  const [accounts, setAccounts] = useState([]);
  const [selAcct, setSelAcct] = useState(() => localStorage.getItem('selectedAccount') || null);
  const [dropOpen, setDropOpen] = useState(false);
  const [date, setDate] = useState(today());
  const [dateMode, setDateMode] = useState('single'); // 'single' or 'range'
  const [rangeStart, setRangeStart] = useState(today());
  const [rangeEnd, setRangeEnd] = useState(today());
  const [loading, setLoading] = useState(false);

  // Stats
  const [actions, setActions] = useState([]);
  const [totalActions, setTotalActions] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [sessionsCount, setSessionsCount] = useState(0);
  const [, setPrivateProfiles] = useState(0);

  // Detail rows (click on stat card)
  const [selectedActionType, setSelectedActionType] = useState('all'); // 'all' or specific type
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Chart data (Graphique tab)
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartRange, setChartRange] = useState(7); // days

  // Sessions
  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsDate, setSessionsDate] = useState(today());
  const [selectedSessionDetail, setSelectedSessionDetail] = useState(null);
  const [sessionActions, setSessionActions] = useState([]);
  const [sessionActionsLoading, setSessionActionsLoading] = useState(false);

  // Targets
  const [targets, setTargets] = useState([]);
  const [targetSearch, setTargetSearch] = useState('');
  const [purposeFilter, setPurposeFilter] = useState(''); // '' | 'interact' | 'scrape'
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ value: '', type: 'user', purpose: 'interact' });
  const [addingTarget, setAddingTarget] = useState(false);
  const [editingWeight, setEditingWeight] = useState({});

  // Expandable target detail
  const [expandedTargetId, setExpandedTargetId] = useState(null);
  const [targetDetail, setTargetDetail] = useState(null);
  const [targetDetailLoading, setTargetDetailLoading] = useState(false);

  const toggleTargetDetail = async (targetId) => {
    if (expandedTargetId === targetId) {
      setExpandedTargetId(null);
      setTargetDetail(null);
      return;
    }
    setExpandedTargetId(targetId);
    setTargetDetailLoading(true);
    try {
      const r = await axios.get(`/api/targets/${targetId}/detailed-stats`);
      setTargetDetail(r.data);
    } catch (e) { setTargetDetail(null); }
    setTargetDetailLoading(false);
  };


  // Cibles - Profile search from DB
  const [, setProfiles] = useState([]);
  const [profileSearch] = useState('');
  const [, setProfilesTotal] = useState(0);
  const [, setProfilesLoading] = useState(false);
  const [filterLocation] = useState('');
  const [filterLang] = useState('');
  const [filterCategory] = useState('');
  const [followersMin] = useState('');
  const [followersMax] = useState('');
  const [, setAvailableCountries] = useState([]);
  const [, setAvailableCategories] = useState([]);

  // ── Actions table: pagination, filters, sort ──
  const [actionsPage, setActionsPage] = useState(1);
  const [actionsTotalPages, setActionsTotalPages] = useState(1);
  const [actionsTotal, setActionsTotal] = useState(0);
  const [actionsSortBy, setActionsSortBy] = useState('timestamp');
  const [actionsSortDir, setActionsSortDir] = useState('desc');
  const [actionsFilterType, setActionsFilterType] = useState('');
  const [actionsFilterSource, setActionsFilterSource] = useState('');
  const [actionsFilterTarget, setActionsFilterTarget] = useState('');
  const [actionsFilterOptions, setActionsFilterOptions] = useState({ types: [], sources: [] });

  // ── Listes tab ──
  const [listSummary, setListSummary] = useState(null);
  const [listSubTab, setListSubTab] = useState('interact'); // 'interact' | 'unfollow' | 'whitelist' | 'blacklist'
  const [interactEntries, setInteractEntries] = useState([]);
  const [interactBySource, setInteractBySource] = useState([]);
  const [interactLoading, setInteractLoading] = useState(false);
  const [interactSourceFilter, setInteractSourceFilter] = useState('');
  const [interactStatusFilter, setInteractStatusFilter] = useState('');
  const [unfollowEntries, setUnfollowEntries] = useState([]);
  const [unfollowLoading, setUnfollowLoading] = useState(false);
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [blacklistEntries, setBlacklistEntries] = useState([]);
  const [fileListLoading, setFileListLoading] = useState(false);
  const [listSearch, setListSearch] = useState('');

  const loadListSummary = useCallback(async () => {
    if (!selAcct) return;
    try {
      const r = await axios.get(`/api/account-lists/summary/${selAcct}`);
      if (r.data.success) setListSummary(r.data);
    } catch {}
  }, [selAcct]);

  const loadInteractList = useCallback(async () => {
    if (!selAcct) return;
    setInteractLoading(true);
    try {
      const r = await axios.get(`/api/account-lists/interact-db/${selAcct}`);
      if (r.data.success) {
        setInteractEntries(r.data.entries || []);
        setInteractBySource(r.data.by_source || []);
      }
    } catch {}
    setInteractLoading(false);
  }, [selAcct]);

  const loadUnfollowList = useCallback(async () => {
    if (!selAcct) return;
    setUnfollowLoading(true);
    try {
      const r = await axios.get(`/api/account-lists/unfollow-db/${selAcct}`);
      if (r.data.success) setUnfollowEntries(r.data.entries || []);
    } catch {}
    setUnfollowLoading(false);
  }, [selAcct]);

  const loadFileLists = useCallback(async () => {
    if (!listSummary?.username) return;
    setFileListLoading(true);
    try {
      const r = await axios.get(`/api/account-lists/${listSummary.username}`);
      if (r.data.success) {
        const wl = [], bl = [];
        for (const list of (r.data.lists || [])) {
          if (list.filename?.includes('whitelist')) {
            for (const e of (list.entries || [])) wl.push({ ...e, file: list.filename });
          }
          if (list.filename?.includes('blacklist')) {
            for (const e of (list.entries || [])) bl.push({ ...e, file: list.filename });
          }
        }
        setWhitelistEntries(wl);
        setBlacklistEntries(bl);
      }
    } catch {}
    setFileListLoading(false);
  }, [listSummary?.username]);

  useEffect(() => {
    if (tab === 'listes' && selAcct) {
      loadListSummary();
      if (listSubTab === 'interact') loadInteractList();
      else if (listSubTab === 'unfollow') loadUnfollowList();
      else loadFileLists();
    }
  }, [tab, selAcct, listSubTab, loadListSummary, loadInteractList, loadUnfollowList, loadFileLists]);

  const handleDeleteInteractEntries = async (usernames) => {
    if (!selAcct || !usernames.length) return;
    try {
      await axios.delete(`/api/account-lists/interact-db/${selAcct}/remove`, { data: { usernames } });
      loadInteractList();
      loadListSummary();
    } catch {}
  };

  const handleClearInteractDone = async () => {
    if (!selAcct) return;
    try {
      await axios.delete(`/api/account-lists/interact-db/${selAcct}/clear`, { data: { onlyDone: true } });
      loadInteractList();
      loadListSummary();
    } catch {}
  };

  const handleClearUnfollowDone = async () => {
    if (!selAcct) return;
    try {
      await axios.delete(`/api/account-lists/unfollow-db/${selAcct}/clear`, { data: { onlyDone: true } });
      loadUnfollowList();
      loadListSummary();
    } catch {}
  };

  const acct = accounts.find(a => a.id?.toString() === selAcct);

  // Sync tab when defaultTab prop changes (sidebar navigation)
  useEffect(() => {
    if (defaultTab) setTab(defaultTab);
  }, [defaultTab]);

  // Sync account when selectedAccountId prop changes (sidebar device → cibles)
  useEffect(() => {
    if (selectedAccountId) {
      const newId = selectedAccountId.toString();
      if (newId !== selAcct) {
        // Clear stale data immediately so user never sees wrong account's data
        setTargets([]);
        setActions([]);
        setDetailRows([]);
        setSessionsList([]);
        setChartData(null);
        setTotalActions(0);
        setUniqueUsers(0);
        setSessionsCount(0);
        setPrivateProfiles(0);
        setExpandedTargetId(null);
        setTargetDetail(null);
      }
      setSelAcct(newId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  // ── Fetch accounts ──
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get('/api/accounts');
        const list = r.data.accounts || [];
        setAccounts(list);
        if (selectedAccountId && list.find(a => a.id.toString() === selectedAccountId.toString())) {
          setSelAcct(selectedAccountId.toString());
        } else if (!selAcct && list.length > 0) {
          setSelAcct(list[0].id.toString());
        }
      } catch (e) { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Fetch data when account or date changes ──
  // Helper: compute query dates based on mode
  const getQueryDates = useCallback(() => {
    if (dateMode === 'range') {
      return { sd: `${rangeStart} 00:00:00`, ed: `${rangeEnd} 23:59:59` };
    }
    return { sd: `${date} 00:00:00`, ed: `${date} 23:59:59` };
  }, [dateMode, date, rangeStart, rangeEnd]);

  const fetchAll = useCallback(async () => {
    if (!selAcct) return;
    setLoading(true);
    localStorage.setItem('selectedAccount', selAcct);
    try {
      const { sd: startDate, ed: endDate } = getQueryDates();

      const [actionsR, sessR] = await Promise.all([
        axios.get(`/api/account-actions/${selAcct}?start_date=${startDate}&end_date=${endDate}`).catch(() => ({ data: {} })),
        axios.get(`/api/sessions-count/${selAcct}?start_date=${startDate}&end_date=${endDate}`).catch(() => ({ data: {} })),
      ]);

      const acts = actionsR.data.actions || [];
      setActions(acts);
      setTotalActions(acts.reduce((s, a) => s + parseInt(a.count || 0), 0));
      setUniqueUsers(actionsR.data.uniqueUsers || 0);
      setPrivateProfiles(actionsR.data.privateProfiles || 0);
      setSessionsCount(sessR.data.count || 0);

      // Auto-load all actions detail on page load (paginated)
      setSelectedActionType('all');
      setActionsPage(1);
      setActionsFilterType(''); setActionsFilterSource(''); setActionsFilterTarget('');
      await fetchAllActions(selAcct, startDate, endDate, 1, 'timestamp', 'desc', '', '', '');
    } catch (e) { /* silent */ }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selAcct, getQueryDates]);

  // ── Fetch all-actions with pagination/filter/sort ──
  const fetchAllActions = async (accountId, sd, ed, page = 1, sortBy = 'timestamp', sortDir = 'desc', fType = '', fSource = '', fTarget = '') => {
    setDetailLoading(true);
    try {
      const params = new URLSearchParams({ start_date: sd, end_date: ed, page, per_page: 500, sort_by: sortBy, sort_dir: sortDir });
      if (fType) params.set('filter_type', fType);
      if (fSource) params.set('filter_source', fSource);
      if (fTarget) params.set('filter_target', fTarget);
      const r = await axios.get(`/api/all-actions/${accountId}?${params}`);
      setDetailRows(r.data.actions || []);
      setActionsTotal(r.data.total || 0);
      setActionsTotalPages(r.data.total_pages || 1);
      setActionsPage(r.data.page || 1);
      if (r.data.filterOptions) {
        setActionsFilterOptions(r.data.filterOptions);
      }
    } catch (_) { setDetailRows([]); }
    setDetailLoading(false);
  };

  // ── Fetch detail rows for a specific action type ──
  const fetchDetailRows = async (actionType) => {
    if (!selAcct) return;
    const { sd, ed } = getQueryDates();
    if (actionType === 'all') {
      setSelectedActionType('all');
      setActionsPage(1);
      setActionsFilterType('');
      await fetchAllActions(selAcct, sd, ed, 1, actionsSortBy, actionsSortDir, '', actionsFilterSource, actionsFilterTarget);
    } else if (actionType === 'uniqueUsers') {
      setDetailLoading(true);
      setSelectedActionType(actionType);
      try {
        const r = await axios.get(`/api/unique-users/${selAcct}?start_date=${sd}&end_date=${ed}&limit=500`);
        setDetailRows(r.data.users || []);
      } catch (_) { setDetailRows([]); }
      setDetailLoading(false);
    } else {
      // Filter by specific type via paginated API
      setSelectedActionType(actionType);
      setActionsPage(1);
      setActionsFilterType(actionType);
      await fetchAllActions(selAcct, sd, ed, 1, actionsSortBy, actionsSortDir, actionType, actionsFilterSource, actionsFilterTarget);
    }
  };

  // ── Handle stat card click ──
  const handleStatCardClick = (actionType) => {
    setExpandedUser(null);
    setExpandedUserActions([]);
    fetchDetailRows(actionType);
  };

  // ── Expandable unique user rows ──
  const [, setExpandedUser] = useState(null);
  const [, setExpandedUserActions] = useState([]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Fetch chart data when switching to chart tab ──
  const fetchChartData = useCallback(async (range) => {
    if (!selAcct) return;
    setChartLoading(true);
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (range || chartRange));
    const sd = start.toISOString().split('T')[0] + ' 00:00:00';
    const ed = end.toISOString().split('T')[0] + ' 23:59:59';
    try {
      const r = await axios.get(`/api/chart-data/${selAcct}?start_date=${sd}&end_date=${ed}`);
      setChartData(r.data);
    } catch (e) { setChartData(null); }
    setChartLoading(false);
  }, [selAcct, chartRange]);

  useEffect(() => {
    if ((tab === 'chart' || tab === 'charts' || tab === 'overview') && selAcct) fetchChartData();
  }, [tab, selAcct, fetchChartData]);

  // ── Fetch targets ──
  const fetchTargets = useCallback(async () => {
    if (!selAcct) return;
    try {
      const { sd, ed } = getQueryDates();
      const r = await axios.get(`/api/account-targets/${selAcct}?start_date=${encodeURIComponent(sd)}&end_date=${encodeURIComponent(ed)}`);
      setTargets(r.data.targets || r.data || []);
    } catch (e) { setTargets([]); }
  }, [selAcct, getQueryDates]);

  useEffect(() => {
    if (!selAcct || tab !== 'targets') return;
    fetchTargets();
  }, [selAcct, tab, fetchTargets]);

  // ── Fetch profiles for Cibles search ──
  const fetchProfiles = useCallback(async () => {
    if (tab !== 'targets') return;
    setProfilesLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (profileSearch) params.set('search', profileSearch);
      if (filterLocation) params.set('country', filterLocation);
      if (filterLang) params.set('language', filterLang);
      if (filterCategory) params.set('category', filterCategory);
      if (followersMin) params.set('followers_min', followersMin);
      if (followersMax) params.set('followers_max', followersMax);
      const r = await axios.get(`/api/profiles-search?${params}`);
      setProfiles(r.data.profiles || []);
      setProfilesTotal(r.data.total || 0);
    } catch (e) { setProfiles([]); }
    setProfilesLoading(false);
  }, [tab, profileSearch, filterLocation, filterLang, filterCategory, followersMin, followersMax]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  // ── Load filter options once ──
  useEffect(() => {
    if (tab !== 'targets') return;
    (async () => {
      try {
        const [catR, countR] = await Promise.all([
          axios.get('/api/categories').catch(() => ({ data: {} })),
          axios.get('/api/countries').catch(() => ({ data: {} })),
        ]);
        setAvailableCategories(catR.data.categories || []);
        setAvailableCountries(countR.data.countries || []);
      } catch (e) { /* silent */ }
    })();
  }, [tab]);

  // ── Add profile as target ──
  // ── Add target from modal ──
  const handleAddTarget = async () => {
    if (!addForm.value.trim() || !selAcct) return;
    setAddingTarget(true);
    try {
      const values = addForm.value.trim().split(/\s+/).filter(Boolean);
      const errors = [];
      let added = 0;
      for (const val of values) {
        try {
          const cleanVal = addForm.type === 'post' ? val : val.replace(/^@/, '').replace(/^#/, '');
          const res = await axios.post('/api/add-target', {
            username: cleanVal,
            accountId: selAcct,
            type: addForm.type,
            purpose: addForm.purpose || 'interact'
          });
          if (res.data.success) added++;
        } catch (error) {
          errors.push(`${val}: ${error.response?.data?.message || error.response?.data?.error || 'Erreur'}`);
        }
      }
      if (added > 0) {
        setShowAddModal(false);
        setAddForm({ value: '', type: 'user', purpose: 'interact' });
        fetchTargets();
      }
      if (errors.length > 0) {
        alert(`${added} cible(s) ajoutée(s), ${errors.length} erreur(s):\n${errors.join('\n')}`);
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data?.error || 'Erreur lors de l\'ajout';
      alert(msg);
    } finally {
      setAddingTarget(false);
    }
  };

  // ── Remove target ──
  const removeTarget = async (targetId) => {
    if (!targetId) return;
    try {
      await axios.delete(`/api/targets/${targetId}`);
      fetchTargets();
    } catch (e) { /* silent */ }
  };

  // ── Toggle target status (active/inactive) ──
  const toggleTargetStatus = async (targetId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTargets(prev => prev.map(t => t.id === targetId ? { ...t, status: newStatus } : t));
    try {
      await axios.put(`/api/update-target-status/${targetId}`, { status: newStatus });
    } catch (e) {
      setTargets(prev => prev.map(t => t.id === targetId ? { ...t, status: currentStatus } : t));
    }
  };

  // ── Update target weight on blur ──
  const handleWeightBlur = async (targetId) => {
    const raw = editingWeight[targetId];
    if (raw === undefined) return;
    const val = raw === '' ? 1 : Math.max(1, parseInt(raw) || 1);
    setTargets(prev => prev.map(t => t.id === targetId ? { ...t, target_weight: val } : t));
    setEditingWeight(prev => { const n = { ...prev }; delete n[targetId]; return n; });
    try {
      await axios.put(`/api/targets/${targetId}/weight`, { weight: val });
    } catch (e) {
      fetchTargets();
    }
  };

  // ── Helpers ──
  const getCount = (type) => {
    const a = actions.find(x => x.action_type === type);
    return parseInt(a?.count || 0);
  };

  const filteredTargets = targets.filter(t => {
    if (targetSearch && !(t.target_profile_name || t.value || '').toLowerCase().includes(targetSearch.toLowerCase())) return false;
    if (purposeFilter && (t.target_purpose || 'interact') !== purposeFilter) return false;
    return true;
  });


  // Sticky pin
  const { sectionRef, getStickyStyle } = useStickyPin('statistics', ['toolbar']);

  return (
    <Page>
      {/* ── Top Bar ── */}
      <TopBar ref={sectionRef('toolbar')} style={getStickyStyle('toolbar')}>
        <Title><BarChart3 size={20} /> Analytiques</Title>
        <Spacer />
        <AccountPicker>
          <PickerBtn onClick={() => setDropOpen(!dropOpen)}>
            {acct?.username && <img src={getLocalProfilePicUrl(acct.username)} alt="" onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getProfilePicUrl(acct.username);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${acct.username}`;return;} e.target.style.display='none'; }} />}
            @{acct?.username || 'Sélectionner'}
            <ChevronDown size={14} />
          </PickerBtn>
          {dropOpen && (
            <PickerDrop>
              {accounts.map(a => (
                <PickerItem key={a.id} $sel={a.id.toString() === selAcct}
                  onClick={() => {
                    const newId = a.id.toString();
                    if (newId !== selAcct) {
                      // Clear stale data immediately before switching
                      setTargets([]);
                      setActions([]);
                      setDetailRows([]);
                      setSessionsList([]);
                      setChartData(null);
                      setTotalActions(0);
                      setUniqueUsers(0);
                      setSessionsCount(0);
                      setPrivateProfiles(0);
                      setExpandedTargetId(null);
                      setTargetDetail(null);
                    }
                    // Always set local state directly for immediate sync
                    setSelAcct(newId);
                    // Also notify parent so sidebar stays in sync
                    if (onSwitchAccount) onSwitchAccount(newId);
                    setDropOpen(false);
                  }}>
                  {a.username && <img src={getLocalProfilePicUrl(a.username)} alt="" onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getLocalProfilePicUrl(a.username);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${a.username}`;return;} e.target.style.display='none'; }} />}
                  @{a.username}
                </PickerItem>
              ))}
            </PickerDrop>
          )}
        </AccountPicker>
        {dateMode === 'single' ? (
          <DateInput type="date" value={date} onChange={e => setDate(e.target.value)} />
        ) : (
          <>
            <DateInput type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={{ width: 130 }} />
            <span style={{ color: 'var(--text-muted)', fontWeight: 700, fontSize: 12 }}>→</span>
            <DateInput type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={{ width: 130 }} />
          </>
        )}
        <SmallBtn onClick={() => { setDateMode(dateMode === 'single' ? 'range' : 'single'); }} title={dateMode === 'single' ? 'Passer en mode période' : 'Passer en mode jour'}>
          <Calendar size={13} /> {dateMode === 'single' ? 'Période' : 'Jour'}
        </SmallBtn>
        <SmallBtn onClick={async () => {
          if (!selAcct) return;
          try {
            const r = await axios.get(`/api/account-oldest-action/${selAcct}`);
            if (r.data.oldest_action) {
              const oldest = new Date(r.data.oldest_action);
              setDateMode('range');
              setRangeStart(oldest.toISOString().split('T')[0]);
              setRangeEnd(today());
            }
          } catch (e) { /* silent */ }
        }} title="Sélectionner toute la période disponible" style={{ background: 'rgba(165,145,239,.08)', borderColor: 'rgba(165,145,239,.3)' }}>
          <Globe size={13} /> Tout
        </SmallBtn>
        <SmallBtn onClick={() => { fetchAll(); if (tab === 'targets') fetchTargets(); }} disabled={loading}>
          <RefreshCw size={13} className={loading ? 'spin' : ''} /> Actualiser
        </SmallBtn>
      </TopBar>

      {/* ── Profile Card ── */}
      {acct && (
        <ProfileCard>
          <ProfileAvatar>
            {acct.username ? <img src={getLocalProfilePicUrl(acct.username)} alt=""
              onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getProfilePicUrl(acct.username);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${acct.username}`;return;} e.target.style.display='none'; }} /> :
              (acct.username || '?')[0].toUpperCase()}
          </ProfileAvatar>
          <ProfileInfo>
            <h3>@{acct.username}</h3>
            <span>ID: {acct.id} &middot; {acct.account_type || acct.type || 'N/A'}</span>
          </ProfileInfo>
          <ProfileStat><div>{acct.posts_count || '—'}</div><div>Posts</div></ProfileStat>
          <ProfileStat><div>{acct.followers_count || '—'}</div><div>Followers</div></ProfileStat>
          <ProfileStat><div>{acct.following_count || '—'}</div><div>Following</div></ProfileStat>
        </ProfileCard>
      )}

      {/* ── Tabs ── */}
      <TabBar>
        <Tab $active={tab === 'overview'} onClick={() => setTab('overview')}>
          <BarChart3 size={14} /> Analytiques
        </Tab>
        <Tab $active={tab === 'targets'} onClick={() => setTab('targets')}>
          <Target size={14} /> Cibles
        </Tab>
        <Tab $active={tab === 'sessions'} onClick={() => setTab('sessions')}>
          <Clock size={14} /> Sessions
        </Tab>
        <Tab $active={tab === 'charts'} onClick={() => setTab('charts')}>
          <TrendingUp size={14} /> Graphiques
        </Tab>
        <Tab $active={tab === 'listes'} onClick={() => setTab('listes')}>
          <List size={14} /> Listes
        </Tab>
      </TabBar>

      {/* ════════ OVERVIEW TAB ════════ */}
      {tab === 'overview' && (() => {
        const totalLikes = getCount('LikeAction');
        const totalFollows = getCount('FollowAction');
        const totalUnfollows = getCount('UnfollowAction');
        const totalProfileViews = getCount('GetProfileAction');
        const totalStoryViews = getCount('StoryWatchAction');
        const totalStoryLikes = getCount('StoryLikeAction');
        const totalComments = getCount('CommentAction');
        const totalDMs = getCount('DirectMessageAction');
        const totalScraped = getCount('ScrapeAction');

        const handleSort = (col) => {
          const newDir = actionsSortBy === col && actionsSortDir === 'desc' ? 'asc' : 'desc';
          setActionsSortBy(col); setActionsSortDir(newDir);
          const { sd, ed } = getQueryDates();
          fetchAllActions(selAcct, sd, ed, 1, col, newDir, actionsFilterType, actionsFilterSource, actionsFilterTarget);
        };
        const handleFilterChange = (fType, fSource, fTarget) => {
          setActionsFilterType(fType); setActionsFilterSource(fSource); setActionsFilterTarget(fTarget);
          setActionsPage(1);
          const { sd, ed } = getQueryDates();
          fetchAllActions(selAcct, sd, ed, 1, actionsSortBy, actionsSortDir, fType, fSource, fTarget);
        };
        const handlePageChange = (p) => {
          setActionsPage(p);
          const { sd, ed } = getQueryDates();
          fetchAllActions(selAcct, sd, ed, p, actionsSortBy, actionsSortDir, actionsFilterType, actionsFilterSource, actionsFilterTarget);
        };
        const sortArrow = (col) => actionsSortBy === col ? (actionsSortDir === 'asc' ? ' ▲' : ' ▼') : '';

        return (
          <>
            {/* ── Stat Cards (full width) ── */}
            <StatsRow>
              <StatCard $active={selectedActionType === 'all'} onClick={() => handleStatCardClick('all')}>
                <StatLabel><Activity size={12} /> Total Actions</StatLabel>
                <StatValue>{totalActions}</StatValue>
                <StatSub>Jour du {new Date(date).toLocaleDateString('fr-FR')}</StatSub>
              </StatCard>
              <StatCard $active={selectedActionType === 'LikeAction'} onClick={() => handleStatCardClick('LikeAction')}>
                <StatLabel><Heart size={12} style={{ color: '#ef4444' }} /> Likes</StatLabel>
                <StatValue>{totalLikes}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'FollowAction'} onClick={() => handleStatCardClick('FollowAction')}>
                <StatLabel><UserPlus size={12} style={{ color: '#22c55e' }} /> Follows</StatLabel>
                <StatValue>{totalFollows}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'UnfollowAction'} onClick={() => handleStatCardClick('UnfollowAction')}>
                <StatLabel><UserMinus size={12} style={{ color: '#f59e0b' }} /> Unfollows</StatLabel>
                <StatValue>{totalUnfollows}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'GetProfileAction'} onClick={() => handleStatCardClick('GetProfileAction')}>
                <StatLabel><Eye size={12} style={{ color: '#a591ef' }} /> Profils vus</StatLabel>
                <StatValue>{totalProfileViews}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'ScrapeAction'} onClick={() => handleStatCardClick('ScrapeAction')}>
                <StatLabel><Scan size={12} style={{ color: '#14b8a6' }} /> Profils scrapés</StatLabel>
                <StatValue>{totalScraped}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'StoryWatchAction'} onClick={() => handleStatCardClick('StoryWatchAction')}>
                <StatLabel><BookOpen size={12} style={{ color: '#f59e0b' }} /> Stories vues</StatLabel>
                <StatValue>{totalStoryViews}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'StoryLikeAction'} onClick={() => handleStatCardClick('StoryLikeAction')}>
                <StatLabel><Star size={12} style={{ color: '#ec4899' }} /> Stories likées</StatLabel>
                <StatValue>{totalStoryLikes}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'CommentAction'} onClick={() => handleStatCardClick('CommentAction')}>
                <StatLabel><MessageCircle size={12} style={{ color: '#3b82f6' }} /> Commentaires</StatLabel>
                <StatValue>{totalComments}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'DirectMessageAction'} onClick={() => handleStatCardClick('DirectMessageAction')}>
                <StatLabel><Send size={12} style={{ color: '#8b5cf6' }} /> DMs envoyés</StatLabel>
                <StatValue>{totalDMs}</StatValue>
              </StatCard>
              <StatCard $active={selectedActionType === 'uniqueUsers'} onClick={() => handleStatCardClick('uniqueUsers')}>
                <StatLabel><Users size={12} style={{ color: '#06b6d4' }} /> Users uniques</StatLabel>
                <StatValue>{uniqueUsers}</StatValue>
              </StatCard>
              <StatCard onClick={() => handleStatCardClick('all')}>
                <StatLabel><Clock size={12} /> Sessions</StatLabel>
                <StatValue>{sessionsCount}</StatValue>
              </StatCard>
            </StatsRow>

            {/* ── Actions table (full width, no collapsible) ── */}
              <SplitPanel $flex="1">
                <DetailTable>
                    <DetailTableHeader>
                      <span>
                        <Activity size={14} />
                        {selectedActionType === 'all' ? 'Toutes les actions' :
                         selectedActionType === 'uniqueUsers' ? 'Users uniques' :
                         (ACTION_MAP[selectedActionType]?.label || selectedActionType)}
                        {' '}({selectedActionType === 'uniqueUsers' ? detailRows.length : actionsTotal})
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {detailLoading && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Chargement...</span>}
                        {(actionsFilterType || actionsFilterSource || actionsFilterTarget) && (
                          <button onClick={() => handleFilterChange('', '', '')} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                            <X size={10} /> Effacer filtres
                          </button>
                        )}
                      </div>
                    </DetailTableHeader>
                    {selectedActionType === 'uniqueUsers' ? (
                      <>
                        {/* Unique users table header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.2fr 0.6fr 0.6fr 1fr', padding: '8px 16px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                          <div>User</div>
                          <div>Source</div>
                          <div>Actions</div>
                          <div>Catégorie</div>
                          <div>Genre</div>
                          <div>Localisation</div>
                        </div>
                        <DetailTableScroll>
                          {detailRows.length === 0 && !detailLoading && (
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                              Aucun user unique pour cette date
                            </div>
                          )}
                          {detailRows.map((row, i) => (
                            <div key={row.target_user || i} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1.2fr 0.6fr 0.6fr 1fr', padding: '7px 16px', alignItems: 'center', borderBottom: '1px solid var(--border)', fontSize: 12, transition: 'background .1s' }}
                              onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              {/* User */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--surface)', border: '2px solid var(--border)' }}>
                                  <img src={row.profile_pic_url || getLocalProfilePicUrl(row.target_user)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getLocalProfilePicUrl(row.target_user);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${row.target_user}`;return;} e.target.style.display='none'; }} />
                                </div>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 3 }}>
                                    @{row.target_user}
                                    {row.is_verified && <Check size={9} style={{ color: '#3b82f6', flexShrink: 0 }} />}
                                    {row.is_private && <Lock size={9} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                                  </div>
                                  {row.full_name && <div style={{ fontSize: 9, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.full_name}</div>}
                                </div>
                              </div>
                              {/* Source */}
                              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{row.source_name || '—'}</div>
                              {/* Actions badges */}
                              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                {parseInt(row.likes) > 0 && <ActionTypeBadge $bg="rgba(239,68,68,.12)" $color="#ef4444"><Heart size={8} /> {row.likes}</ActionTypeBadge>}
                                {parseInt(row.follows) > 0 && <ActionTypeBadge $bg="rgba(34,197,94,.12)" $color="#22c55e"><UserPlus size={8} /> {row.follows}</ActionTypeBadge>}
                                {parseInt(row.story_likes) > 0 && <ActionTypeBadge $bg="rgba(236,72,153,.12)" $color="#ec4899"><Star size={8} /> {row.story_likes}</ActionTypeBadge>}
                                {parseInt(row.story_watches) > 0 && <ActionTypeBadge $bg="rgba(245,158,11,.12)" $color="#f59e0b"><BookOpen size={8} /> {row.story_watches}</ActionTypeBadge>}
                                {parseInt(row.comments) > 0 && <ActionTypeBadge $bg="rgba(59,130,246,.12)" $color="#3b82f6"><MessageCircle size={8} /> {row.comments}</ActionTypeBadge>}
                                {parseInt(row.dms) > 0 && <ActionTypeBadge $bg="rgba(139,92,246,.12)" $color="#8b5cf6"><Send size={8} /> {row.dms}</ActionTypeBadge>}
                                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 800, color: 'var(--primary)', marginLeft: 2 }}>{row.total_actions}</span>
                              </div>
                              {/* Category */}
                              <div>{row.pv_category ? <ProfileBadge $bg="rgba(165,145,239,.1)" $color="#a591ef"><Tag size={8} /> {row.pv_category}</ProfileBadge> : <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>}</div>
                              {/* Gender */}
                              <div>{row.pv_gender ? <ProfileBadge $bg="rgba(59,130,246,.1)" $color="#3b82f6"><UserIcon size={8} /> {row.pv_gender}</ProfileBadge> : <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>}</div>
                              {/* Location */}
                              <div>{(row.pv_city || row.pv_country) ? <ProfileBadge $bg="rgba(34,197,94,.1)" $color="#22c55e"><MapPin size={8} /> {[row.pv_city, row.pv_country].filter(Boolean).join(', ')}</ProfileBadge> : <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>—</span>}</div>
                            </div>
                          ))}
                        </DetailTableScroll>
                      </>
                    ) : (
                      <>
                        {/* Sortable headers */}
                        <DetailTableHead>
                          <SortHeader onClick={() => handleSort('timestamp')}>Timestamp{sortArrow('timestamp')}</SortHeader>
                          <SortHeader onClick={() => handleSort('action_type')}>Type{sortArrow('action_type')}</SortHeader>
                          <SortHeader onClick={() => handleSort('source_name')}>Source{sortArrow('source_name')}</SortHeader>
                          <SortHeader onClick={() => handleSort('target_user')}>Target User{sortArrow('target_user')}</SortHeader>
                          <SortHeader onClick={() => handleSort('pv_category')}>Profil{sortArrow('pv_category')}</SortHeader>
                        </DetailTableHead>
                        {/* Filter dropdowns */}
                        <FilterRow>
                          <div></div>
                          <select value={actionsFilterType} onChange={e => handleFilterChange(e.target.value, actionsFilterSource, actionsFilterTarget)}>
                            <option value="">Tous les types</option>
                            {actionsFilterOptions.types.map(t => {
                              const m = ACTION_MAP[t];
                              return <option key={t} value={t}>{m ? m.label : t}</option>;
                            })}
                          </select>
                          <select value={actionsFilterSource} onChange={e => handleFilterChange(actionsFilterType, e.target.value, actionsFilterTarget)}>
                            <option value="">Toutes les sources</option>
                            {actionsFilterOptions.sources.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          <input
                            type="text" placeholder="Filtrer target..."
                            value={actionsFilterTarget}
                            onChange={e => {
                              setActionsFilterTarget(e.target.value);
                            }}
                            onKeyDown={e => { if (e.key === 'Enter') handleFilterChange(actionsFilterType, actionsFilterSource, actionsFilterTarget); }}
                            onBlur={() => handleFilterChange(actionsFilterType, actionsFilterSource, actionsFilterTarget)}
                          />
                          <div></div>
                        </FilterRow>
                        <DetailTableScroll>
                          {detailRows.length === 0 && !detailLoading && (
                            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                              Aucune action pour cette date
                            </div>
                          )}
                          {detailRows.map((row, i) => {
                            const actionType = row.action_type || row.type || '';
                            const meta = ACTION_MAP[actionType] || {};
                            const Icon = meta.icon || Activity;
                            return (
                              <DetailTableRow key={row.id || i}>
                                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: 'var(--text-muted)' }}>
                                  {row.timestamp ? new Date(row.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                                </div>
                                <div><ActionTypeBadge $bg={meta.bg} $color={meta.color}><Icon size={10} /> {meta.label || actionType}</ActionTypeBadge></div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{row.source_name || row.source || '—'}</div>
                                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{row.target_user ? `@${row.target_user}` : '—'}</div>
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                  {row.pv_category && <ProfileBadge $bg="rgba(165,145,239,.1)" $color="#a591ef"><Tag size={8} /> {row.pv_category}</ProfileBadge>}
                                  {row.pv_gender && <ProfileBadge $bg="rgba(59,130,246,.1)" $color="#3b82f6"><UserIcon size={8} /> {row.pv_gender}</ProfileBadge>}
                                  {(row.pv_city || row.pv_country) && <ProfileBadge $bg="rgba(34,197,94,.1)" $color="#22c55e"><MapPin size={8} /> {[row.pv_city, row.pv_country].filter(Boolean).join(', ')}</ProfileBadge>}
                                </div>
                              </DetailTableRow>
                            );
                          })}
                        </DetailTableScroll>
                        {/* Pagination */}
                        {actionsTotalPages > 1 && (
                          <PaginationBar>
                            <span>Page {actionsPage} / {actionsTotalPages} — {actionsTotal} actions au total</span>
                            <PaginationBtns>
                              <button disabled={actionsPage <= 1} onClick={() => handlePageChange(1)}>«</button>
                              <button disabled={actionsPage <= 1} onClick={() => handlePageChange(actionsPage - 1)}>‹</button>
                              {Array.from({ length: Math.min(5, actionsTotalPages) }, (_, idx) => {
                                let p;
                                if (actionsTotalPages <= 5) { p = idx + 1; }
                                else if (actionsPage <= 3) { p = idx + 1; }
                                else if (actionsPage >= actionsTotalPages - 2) { p = actionsTotalPages - 4 + idx; }
                                else { p = actionsPage - 2 + idx; }
                                return <button key={p} data-active={p === actionsPage ? 'true' : undefined} onClick={() => handlePageChange(p)}>{p}</button>;
                              })}
                              <button disabled={actionsPage >= actionsTotalPages} onClick={() => handlePageChange(actionsPage + 1)}>›</button>
                              <button disabled={actionsPage >= actionsTotalPages} onClick={() => handlePageChange(actionsTotalPages)}>»</button>
                            </PaginationBtns>
                          </PaginationBar>
                        )}
                      </>
                    )}
                  </DetailTable>
              </SplitPanel>
          </>
        );
      })()}

      {/* ════════ CHARTS / GRAPHIQUES TAB ════════ */}
      {tab === 'charts' && (() => {
        // ── Performance metrics computation (same as overview) ──
        const totalLikes = getCount('LikeAction');
        const totalFollows = getCount('FollowAction');
        const totalUnfollows = getCount('UnfollowAction');
        const totalProfileViews = getCount('GetProfileAction');
        const totalStoryViews = getCount('StoryWatchAction');
        const totalStoryLikes = getCount('StoryLikeAction');
        const totalComments = getCount('CommentAction');

        const fHist = chartData?.followers_history || [];
        const validF = fHist.filter(v => v > 0);
        const fFirst = validF[0] || 0;
        const fLast = validF[validF.length - 1] || 0;
        const fDelta = fLast - fFirst;
        const fDays = validF.length > 1 ? validF.length - 1 : 1;
        const growthPerDay = fFirst > 0 ? (fDelta / fDays).toFixed(1) : '—';

        const chartLikesTotal = (chartData?.actions_by_type?.LikeAction || []).reduce((s, v) => s + v, 0);
        const chartFollowsTotal = (chartData?.actions_by_type?.FollowAction || []).reduce((s, v) => s + v, 0);

        const followersPerLikes = chartLikesTotal > 0 ? ((fDelta / chartLikesTotal) * 100).toFixed(1) : '—';
        const followBackRate = chartFollowsTotal > 0 ? ((fDelta / chartFollowsTotal) * 100).toFixed(1) : '—';
        const engagementRate = totalProfileViews > 0 ? (((totalLikes + totalComments) / totalProfileViews) * 100).toFixed(1) : '—';
        const unfollowRatio = totalFollows > 0 ? ((totalUnfollows / totalFollows) * 100).toFixed(0) : '—';
        const storyEngagement = totalStoryViews > 0 ? ((totalStoryLikes / totalStoryViews) * 100).toFixed(1) : '—';
        const actionsPerSession = sessionsCount > 0 ? (totalActions / sessionsCount).toFixed(0) : '—';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* ── Performance Metrics ── */}
            <MetricsGrid>
              <MetricCard $color="#22c55e">
                <div className="mc-label"><TrendingUp size={10} /> Croissance / jour</div>
                <div className="mc-value">{growthPerDay === '—' ? '—' : (growthPerDay > 0 ? '+' : '') + growthPerDay}</div>
                <div className="mc-sub">followers/jour sur {chartRange}j</div>
              </MetricCard>
              <MetricCard $color="#a591ef">
                <div className="mc-label"><Heart size={10} /> Efficacité Likes</div>
                <div className="mc-value">{followersPerLikes}</div>
                <div className="mc-sub">followers / 100 likes</div>
              </MetricCard>
              <MetricCard $color="#3b82f6">
                <div className="mc-label"><UserPlus size={10} /> Follow-back</div>
                <div className="mc-value">{followBackRate}{followBackRate !== '—' ? '%' : ''}</div>
                <div className="mc-sub">followers gagnés / follows faits</div>
              </MetricCard>
              <MetricCard $color="#f59e0b">
                <div className="mc-label"><Eye size={10} /> Engagement</div>
                <div className="mc-value">{engagementRate}{engagementRate !== '—' ? '%' : ''}</div>
                <div className="mc-sub">(likes+comments) / profils vus</div>
              </MetricCard>
              <MetricCard $color="#ec4899">
                <div className="mc-label"><Heart size={10} /> Story Engage.</div>
                <div className="mc-value">{storyEngagement}{storyEngagement !== '—' ? '%' : ''}</div>
                <div className="mc-sub">story likes / story views</div>
              </MetricCard>
              <MetricCard>
                <div className="mc-label"><Activity size={10} /> Actions/Session</div>
                <div className="mc-value">{actionsPerSession}</div>
                <div className="mc-sub">{totalActions} actions / {sessionsCount} sessions</div>
              </MetricCard>
              <MetricCard $color="#ef4444">
                <div className="mc-label"><UserMinus size={10} /> Ratio Unfollow</div>
                <div className="mc-value">{unfollowRatio}{unfollowRatio !== '—' ? '%' : ''}</div>
                <div className="mc-sub">unfollows / follows (jour)</div>
              </MetricCard>
            </MetricsGrid>

            {/* ── Range Selector ── */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {[7, 14, 30, 60].map(d => (
                <SmallBtn key={d} onClick={() => { setChartRange(d); fetchChartData(d); }}
                  style={chartRange === d ? { borderColor: 'var(--primary)', color: 'var(--primary)' } : {}}>
                  {d}j
                </SmallBtn>
              ))}
              {chartLoading && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>Chargement...</span>}
            </div>

            {/* ── Charts ── */}
            {chartData && chartData.dates && chartData.dates.length > 0 ? (<>
              {/* Actions Bar Chart */}
              <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <BarChart3 size={14} /> Actions par jour
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData.dates.map((d, i) => ({
                    date: d.split('-').slice(1).join('/'),
                    Likes: chartData.actions_by_type?.LikeAction?.[i] || 0,
                    Follows: chartData.actions_by_type?.FollowAction?.[i] || 0,
                    Unfollows: chartData.actions_by_type?.UnfollowAction?.[i] || 0,
                    'Story Views': chartData.actions_by_type?.StoryWatchAction?.[i] || 0,
                    'Story Likes': chartData.actions_by_type?.StoryLikeAction?.[i] || 0,
                    'Profils vus': chartData.actions_by_type?.GetProfileAction?.[i] || 0,
                    Comments: chartData.actions_by_type?.CommentAction?.[i] || 0,
                  }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="Likes" stackId="a" fill="#ef4444" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Follows" stackId="a" fill="#22c55e" />
                    <Bar dataKey="Unfollows" stackId="a" fill="#f59e0b" />
                    <Bar dataKey="Story Views" stackId="a" fill="#f59e0b" opacity={0.5} />
                    <Bar dataKey="Story Likes" stackId="a" fill="#ec4899" />
                    <Bar dataKey="Profils vus" stackId="a" fill="#a591ef" />
                    <Bar dataKey="Comments" stackId="a" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Followers / Following Line Chart */}
              {(chartData.followers_history?.some(v => v > 0) || chartData.following_history?.some(v => v > 0)) && (
                <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, padding: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TrendingUp size={14} /> Followers / Following
                  </div>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={chartData.dates.map((d, i) => ({
                      date: d.split('-').slice(1).join('/'),
                      Followers: chartData.followers_history?.[i] || 0,
                      Following: chartData.following_history?.[i] || 0,
                      Posts: chartData.posts_history?.[i] || 0,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,.05)" />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                      <YAxis tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                      <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="Followers" stroke="#a591ef" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Following" stroke="#22c55e" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Posts" stroke="#f59e0b" strokeWidth={1} dot={false} strokeDasharray="5 5" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Summary stats */}
              {chartData.followers_history && (
                <StatsRow>
                  <StatCard>
                    <StatLabel><Users size={12} style={{ color: '#a591ef' }} /> Followers</StatLabel>
                    <StatValue>{fLast || '—'}</StatValue>
                    {fDelta !== 0 && <StatSub style={{ color: fDelta > 0 ? '#22c55e' : '#ef4444' }}>{fDelta > 0 ? '+' : ''}{fDelta} sur {chartRange}j</StatSub>}
                  </StatCard>
                  <StatCard>
                    <StatLabel><UserPlus size={12} style={{ color: '#22c55e' }} /> Following</StatLabel>
                    <StatValue>{chartData.following_history[chartData.following_history.length - 1] || '—'}</StatValue>
                  </StatCard>
                  <StatCard>
                    <StatLabel><FileText size={12} style={{ color: '#f59e0b' }} /> Posts</StatLabel>
                    <StatValue>{chartData.posts_history[chartData.posts_history.length - 1] || '—'}</StatValue>
                  </StatCard>
                </StatsRow>
              )}
            </>) : !chartLoading ? (
              <EmptyState><BarChart3 size={32} /><p>Aucune donnée pour cette période</p></EmptyState>
            ) : null}
          </div>
        );
      })()}

      {/* ════════ TARGETS / CIBLES TAB ════════ */}
      {tab === 'targets' && (() => {
        // Deduplicate stats by unique target name (avoid double-counting scrape+interact for same user)
        const seen = new Set();
        let totalInteractions = 0, totalLikes = 0, totalProfileVisits = 0;
        filteredTargets.forEach(t => {
          const key = (t.target_profile_name || '').toLowerCase();
          if (!seen.has(key)) {
            seen.add(key);
            totalInteractions += (t.total_interactions || 0);
            totalLikes += (t.like_count || 0);
            totalProfileVisits += (t.profile_visit_count || 0);
          }
        });
        const activeCibles = filteredTargets.filter(t => t.status === 'active').length;
        const interactCount = targets.filter(t => (t.target_purpose || 'interact') === 'interact').length;
        const scrapeCount = targets.filter(t => t.target_purpose === 'scrape').length;
        const avgInteractions = filteredTargets.length > 0 ? (totalInteractions / seen.size).toFixed(1) : '0';
        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

        return (
          <>
            {/* ── Stats Cards ── */}
            <CiblesStatsRow>
              <CiblesStatCard>
                <div className="cs-label"><Target size={11} /> Total Cibles</div>
                <div className="cs-value">{targets.length}</div>
              </CiblesStatCard>
              <CiblesStatCard $color="#22c55e">
                <div className="cs-label"><Check size={11} /> Cibles Actives</div>
                <div className="cs-value">{activeCibles}</div>
              </CiblesStatCard>
              <CiblesStatCard $color="var(--primary)">
                <div className="cs-label"><TrendingUp size={11} /> Total Interactions</div>
                <div className="cs-value">{totalInteractions}</div>
              </CiblesStatCard>
              <CiblesStatCard>
                <div className="cs-label"><Activity size={11} /> Moy. / Cible</div>
                <div className="cs-value">{avgInteractions}</div>
              </CiblesStatCard>
              <CiblesStatCard $color="#ef4444">
                <div className="cs-label"><Heart size={11} /> Total Likes</div>
                <div className="cs-value">{totalLikes}</div>
              </CiblesStatCard>
              <CiblesStatCard $color="#3b82f6">
                <div className="cs-label"><Eye size={11} /> Total Profil Visits</div>
                <div className="cs-value">{totalProfileVisits}</div>
              </CiblesStatCard>
            </CiblesStatsRow>

            {/* ── Filters ── */}
            <CiblesFilterRow>
              <AddTargetTopBtn onClick={() => setShowAddModal(true)}>
                <Plus size={14} /> Ajouter une cible
              </AddTargetTopBtn>
              <FilterSel style={{ minWidth: 130 }}>
                <option value="">Compte: @{acct?.username || '...'}</option>
              </FilterSel>
              <FilterInputSmall style={{ width: 160 }} placeholder="Filtrer par cible..."
                value={targetSearch} onChange={e => setTargetSearch(e.target.value)} />
              <FilterSel value={purposeFilter} onChange={e => setPurposeFilter(e.target.value)}>
                <option value="">Type: Tous ({interactCount}i / {scrapeCount}s)</option>
                <option value="interact">Interaction ({interactCount})</option>
                <option value="scrape">Scraping ({scrapeCount})</option>
              </FilterSel>
              <FilterSel value="" onChange={() => {}}>
                <option value="">Status: Tous</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </FilterSel>
            </CiblesFilterRow>

            {/* ── Table ── */}
            {filteredTargets.length === 0 ? (
              <EmptyState><Target size={32} /><p>Aucune cible trouvée</p></EmptyState>
            ) : (
              <CiblesTable>
                <thead>
                  <tr>
                    <th>Cible</th>
                    <th style={{ textAlign: 'center' }}>Followers</th>
                    <th>Type</th>
                    <th>But</th>
                    <th>Poids</th>
                    <th style={{ textAlign: 'center' }}>St.</th>
                    <th style={{ textAlign: 'center' }}>Total</th>
                    <th style={{ textAlign: 'center', color: '#ef4444' }} title="Likes"><Heart size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#22c55e' }} title="Follows"><UserPlus size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#a591ef' }} title="Profils vus"><Eye size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#f59e0b' }} title="Stories vues"><BookOpen size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#3b82f6' }} title="Commentaires"><MessageCircle size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#06b6d4' }} title="Scrapes"><Scan size={11} /></th>
                    <th style={{ textAlign: 'center' }} title="Users uniques"><Users size={11} /></th>
                    <th style={{ textAlign: 'center', color: '#ef4444' }} title="Profils privés"><Lock size={11} /></th>
                    <th>Dernière Action</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTargets.map((t, i) => {
                    const isExpanded = expandedTargetId === t.id;
                    const ACTION_DETAIL_META = {
                      'LikeAction': { label: 'Likes', icon: Heart, color: '#ef4444', bg: 'rgba(239,68,68,.1)' },
                      'FollowAction': { label: 'Follows', icon: UserPlus, color: '#22c55e', bg: 'rgba(34,197,94,.1)' },
                      'UnfollowAction': { label: 'Unfollows', icon: UserMinus, color: '#f59e0b', bg: 'rgba(245,158,11,.1)' },
                      'GetProfileAction': { label: 'Profils vus', icon: Eye, color: '#a591ef', bg: 'rgba(165,145,239,.1)' },
                      'StoryWatchAction': { label: 'Stories vues', icon: BookOpen, color: '#f59e0b', bg: 'rgba(245,158,11,.1)' },
                      'StoryLikeAction': { label: 'Stories likées', icon: Star, color: '#ec4899', bg: 'rgba(236,72,153,.1)' },
                      'CommentAction': { label: 'Commentaires', icon: MessageCircle, color: '#3b82f6', bg: 'rgba(59,130,246,.1)' },
                      'ScrapeAction': { label: 'Scrapes', icon: Scan, color: '#06b6d4', bg: 'rgba(6,182,212,.1)' },
                      'DirectMessageAction': { label: 'DMs', icon: Send, color: '#8b5cf6', bg: 'rgba(139,92,246,.1)' },
                    };
                    const fmtNum = (n) => {
                      if (!n || n === 0) return '—';
                      if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
                      if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
                      return n.toString();
                    };
                    return (
                    <React.Fragment key={t.id || i}>
                    <tr style={{ cursor: 'pointer', background: isExpanded ? 'rgba(165,145,239,.06)' : undefined }}
                      onClick={() => toggleTargetDetail(t.id)}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ transition: 'transform .2s', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', flexShrink: 0 }}>
                            <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />
                          </div>
                          <div style={{ width: 28, height: 28, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, background: 'var(--surface)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {t.target_profile_name ? (
                              <img src={getLocalProfilePicUrl(t.target_profile_name)} alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={e => {
                                  const step = parseInt(e.target.dataset.fb || '0');
                                  if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(t.target_profile_name); return; }
                                  if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${t.target_profile_name}`; return; }
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                                }} />
                            ) : null}
                            <span style={{ display: t.target_profile_name ? 'none' : 'flex', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                              {(t.target_profile_name || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                            {(t.target_type === 'post' || t.target_type === 'post_url') ? '📌 ' : '@'}{(t.target_type === 'post' || t.target_type === 'post_url') ? (t.target_profile_name?.match(/\/p\/([A-Za-z0-9_-]+)/)?.[1] || t.target_profile_name || '—').substring(0, 24) : (t.target_profile_name || '—')}
                          </span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 600, color: t.target_followers > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {fmtNum(t.target_followers)}
                      </td>
                      <td style={{ fontSize: 11 }}>{t.target_type === 'post' ? 'post-likers' : (t.target_type || 'user')}</td>
                      <td>
                        <span style={{
                          fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                          background: (t.target_purpose || 'interact') === 'scrape' ? 'rgba(245,158,11,.12)' : 'rgba(34,197,94,.12)',
                          color: (t.target_purpose || 'interact') === 'scrape' ? '#f59e0b' : '#22c55e',
                          textTransform: 'uppercase', letterSpacing: '.3px'
                        }}>
                          {(t.target_purpose || 'interact') === 'scrape' ? 'Scrape' : 'Interact'}
                        </span>
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <WeightInput
                          type="number"
                          min="1"
                          value={editingWeight[t.id] !== undefined ? editingWeight[t.id] : (t.target_weight || 1)}
                          onChange={e => setEditingWeight(prev => ({ ...prev, [t.id]: e.target.value }))}
                          onBlur={() => handleWeightBlur(t.id)}
                          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </td>
                      <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
                        <TargetStatusDot
                          $active={t.status === 'active'}
                          onClick={() => toggleTargetStatus(t.id, t.status)}
                          title={t.status === 'active' ? 'Cliquer pour désactiver' : 'Cliquer pour activer'}
                        />
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
                        {t.total_interactions || 0}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.like_count > 0 ? '#ef4444' : 'var(--text-muted)' }}>
                        {t.like_count > 0 ? t.like_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.follow_count > 0 ? '#22c55e' : 'var(--text-muted)' }}>
                        {t.follow_count > 0 ? t.follow_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.profile_visit_count > 0 ? '#a591ef' : 'var(--text-muted)' }}>
                        {t.profile_visit_count > 0 ? t.profile_visit_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.story_watch_count > 0 ? '#f59e0b' : 'var(--text-muted)' }}>
                        {t.story_watch_count > 0 ? t.story_watch_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.comment_count > 0 ? '#3b82f6' : 'var(--text-muted)' }}>
                        {t.comment_count > 0 ? t.comment_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.scrape_count > 0 ? '#06b6d4' : 'var(--text-muted)' }}>
                        {t.scrape_count > 0 ? t.scrape_count : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.unique_users > 0 ? 'var(--primary)' : 'var(--text-muted)' }}>
                        {t.unique_users > 0 ? t.unique_users : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: t.private_profiles > 0 ? '#ef4444' : 'var(--text-muted)' }}>
                        {t.private_profiles > 0 ? t.private_profiles : '—'}
                      </td>
                      <td style={{ fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(t.last_interaction)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <RemoveBtn title="Retirer" onClick={() => removeTarget(t.id)}>
                          <X size={10} />
                        </RemoveBtn>
                      </td>
                    </tr>
                    {/* ── Expanded detail panel ── */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={17} style={{ padding: 0 }}>
                          <div style={{ background: 'rgba(165,145,239,.03)', borderTop: '1px solid rgba(165,145,239,.15)', borderBottom: '2px solid rgba(165,145,239,.15)', padding: '16px 20px' }}>
                            {targetDetailLoading ? (
                              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>
                                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> Chargement des détails...
                              </div>
                            ) : !targetDetail ? (
                              <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>Aucune donnée disponible</div>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {/* ── Row 1: Interaction breakdown + Profile counts ── */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                                  {/* Interactions by type */}
                                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Activity size={13} style={{ color: 'var(--primary)' }} /> Répartition des interactions
                                    </div>
                                    {targetDetail.interactions?.length > 0 ? targetDetail.interactions.map(int => {
                                      const m = ACTION_DETAIL_META[int.type] || { label: int.type, color: 'var(--text-muted)', bg: 'var(--surface)', icon: Activity };
                                      const Icon = m.icon;
                                      const totalInt = targetDetail.interactions.reduce((s, x) => s + x.count, 0);
                                      const pct = totalInt > 0 ? ((int.count / totalInt) * 100).toFixed(0) : 0;
                                      return (
                                        <div key={int.type} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 100 }}>
                                            <Icon size={11} style={{ color: m.color }} />
                                            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)' }}>{m.label}</span>
                                          </div>
                                          <div style={{ flex: 1, height: 6, background: 'var(--surface)', borderRadius: 3, overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${pct}%`, background: m.color, borderRadius: 3, transition: 'width .3s' }} />
                                          </div>
                                          <span style={{ fontSize: 11, fontWeight: 800, color: m.color, fontFamily: "'JetBrains Mono', monospace", minWidth: 30, textAlign: 'right' }}>{int.count}</span>
                                          <span style={{ fontSize: 9, color: 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>{pct}%</span>
                                        </div>
                                      );
                                    }) : <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Aucune interaction</div>}
                                  </div>

                                  {/* Gender breakdown */}
                                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Users size={13} style={{ color: '#ec4899' }} /> Répartition Homme / Femme
                                    </div>
                                    {(() => {
                                      const gd = targetDetail.gender || [];
                                      const total = gd.reduce((s, g) => s + g.count, 0);
                                      const genderColors = { 'male': '#3b82f6', 'female': '#ec4899', 'unknown': '#94a3b8' };
                                      const genderLabels = { 'male': 'Hommes', 'female': 'Femmes', 'unknown': 'Non déterminé' };
                                      const genderIcons = { 'male': '♂', 'female': '♀', 'unknown': '?' };
                                      if (total === 0) return <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Aucune donnée d'analyse</div>;
                                      return (
                                        <>
                                          {/* Visual bar */}
                                          <div style={{ display: 'flex', height: 18, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                                            {gd.filter(g => g.count > 0).map(g => (
                                              <div key={g.gender} style={{ width: `${(g.count / total) * 100}%`, background: genderColors[g.gender] || '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <span style={{ fontSize: 9, fontWeight: 800, color: '#fff' }}>{((g.count / total) * 100).toFixed(0)}%</span>
                                              </div>
                                            ))}
                                          </div>
                                          {gd.map(g => (
                                            <div key={g.gender} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                              <span style={{ fontSize: 14, width: 20, textAlign: 'center' }}>{genderIcons[g.gender] || '?'}</span>
                                              <span style={{ fontSize: 11, fontWeight: 600, color: genderColors[g.gender] || '#94a3b8', flex: 1 }}>{genderLabels[g.gender] || g.gender}</span>
                                              <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: genderColors[g.gender] || '#94a3b8' }}>{g.count}</span>
                                              <span style={{ fontSize: 10, color: 'var(--text-muted)', minWidth: 35, textAlign: 'right' }}>{((g.count / total) * 100).toFixed(1)}%</span>
                                            </div>
                                          ))}
                                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)' }}>Total profils analysés</span>
                                            <span style={{ fontSize: 12, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: 'var(--primary)' }}>{total}</span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>

                                  {/* Profile counts & categories */}
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {/* Profile counts card */}
                                    <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', padding: 14 }}>
                                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Scan size={13} style={{ color: '#06b6d4' }} /> Comptes analysés
                                      </div>
                                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                                        {[
                                          { label: 'Profils analysés', val: targetDetail.profile_counts?.total_analyzed, color: '#a591ef' },
                                          { label: 'Analyse Gemini', val: targetDetail.profile_counts?.gemini_analyzed, color: '#f59e0b' },
                                          { label: 'Profils scrapés', val: targetDetail.profile_counts?.scraped, color: '#06b6d4' },
                                          { label: 'Profils privés', val: targetDetail.profile_counts?.private_profiles, color: '#ef4444' },
                                        ].map(s => (
                                          <div key={s.label} style={{ padding: '6px 8px', borderRadius: 6, background: `${s.color}11` }}>
                                            <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{s.val || 0}</div>
                                            <div style={{ fontSize: 8, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>{s.label}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    {/* Categories card */}
                                    {targetDetail.categories?.length > 0 && (
                                      <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', padding: 14, flex: 1 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <Tag size={13} style={{ color: '#f59e0b' }} /> Catégories de comptes
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 120, overflowY: 'auto' }}>
                                          {targetDetail.categories.map(c => (
                                            <div key={c.category} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                              <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.category}</span>
                                              <span style={{ fontSize: 11, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: 'var(--primary)' }}>{c.count}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* ── Row 2: Per-account interactions table ── */}
                                {targetDetail.per_account?.length > 0 && (
                                  <div style={{ background: 'var(--card-bg)', borderRadius: 10, border: '1px solid var(--border)', padding: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <Instagram size={13} style={{ color: 'var(--primary)' }} /> Interactions par compte Instagram
                                    </div>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                                            <th style={{ textAlign: 'left', padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Compte</th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>Total</th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#ef4444' }}><Heart size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#22c55e' }}><UserPlus size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#a591ef' }}><Eye size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#f59e0b' }}><BookOpen size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#ec4899' }}><Star size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#3b82f6' }}><MessageCircle size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: '#06b6d4' }}><Scan size={10} /></th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>Profils uniques</th>
                                            <th style={{ textAlign: 'center', padding: '6px 4px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>Analysés</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {targetDetail.per_account.map(pa => {
                                            const analyzed = targetDetail.per_account_analyzed?.find(a => a.account_username === pa.account_username);
                                            return (
                                              <tr key={pa.account_id} style={{ borderBottom: '1px solid var(--border)' }}>
                                                <td style={{ padding: '6px 10px' }}>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <div style={{ width: 20, height: 20, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '1px solid var(--border)' }}>
                                                      <img src={getLocalProfilePicUrl(pa.account_username)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        onError={e => { e.target.style.display='none'; }} />
                                                    </div>
                                                    <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 11 }}>@{pa.account_username}</span>
                                                  </div>
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: 800, color: 'var(--text-primary)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.total_actions}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.likes > 0 ? '#ef4444' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.likes || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.follows > 0 ? '#22c55e' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.follows || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.profile_views > 0 ? '#a591ef' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.profile_views || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.story_views > 0 ? '#f59e0b' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.story_views || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.story_likes > 0 ? '#ec4899' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.story_likes || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.comments > 0 ? '#3b82f6' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.comments || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: pa.scraped > 0 ? '#06b6d4' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.scraped || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--text-secondary)', fontFamily: "'JetBrains Mono', monospace" }}>{pa.unique_profiles || '—'}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700, color: analyzed?.total_analyzed > 0 ? '#f59e0b' : 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                                                  {analyzed?.total_analyzed || '—'}
                                                  {analyzed?.gemini_analyzed > 0 && <span style={{ fontSize: 8, color: '#f59e0b', marginLeft: 2 }}>({analyzed.gemini_analyzed})</span>}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </CiblesTable>
            )}

            {/* ── Pagination ── */}
            <CiblesPagination>
              <span>{filteredTargets.length} cibles au total &middot; Page 1 sur 1</span>
              <div className="pg-nums">
                <button className="pg-active">1</button>
              </div>
            </CiblesPagination>

            {/* ── Add Target Modal ── */}
            {showAddModal && (
              <ModalOverlay onClick={() => setShowAddModal(false)}>
                <ModalBox onClick={e => e.stopPropagation()}>
                  <ModalHeader>
                    <ModalTitle><Plus size={20} /> Ajouter une cible</ModalTitle>
                    <ModalCloseBtn onClick={() => setShowAddModal(false)}><X size={18} /></ModalCloseBtn>
                  </ModalHeader>
                  <ModalBody>
                    <ModalField>
                      <ModalLabel>Compte associé</ModalLabel>
                      <ModalAcctInfo>
                        {acct?.username && <img src={getLocalProfilePicUrl(acct.username)} alt="" onError={e => { const step=parseInt(e.target.dataset.fb||'0'); if(step===0){e.target.dataset.fb='1';e.target.src=getProfilePicUrl(acct.username);return;} if(step===1){e.target.dataset.fb='2';e.target.src=`https://unavatar.io/instagram/${acct.username}`;return;} e.target.style.display='none'; }} />}
                        <span>@{acct?.username || '...'}</span>
                      </ModalAcctInfo>
                    </ModalField>
                    <ModalField>
                      <ModalLabel>Type de cible</ModalLabel>
                      <ModalTypeGrid>
                        <ModalTypeBtn $active={addForm.type === 'user'} onClick={() => setAddForm(f => ({ ...f, type: 'user' }))}>
                          <UserIcon size={22} /> Utilisateur
                        </ModalTypeBtn>
                        <ModalTypeBtn $active={addForm.type === 'hashtag'} onClick={() => setAddForm(f => ({ ...f, type: 'hashtag' }))}>
                          <Hash size={22} /> Hashtag
                        </ModalTypeBtn>
                        <ModalTypeBtn $active={addForm.type === 'post'} onClick={() => setAddForm(f => ({ ...f, type: 'post' }))}>
                          <FileText size={22} /> Post
                        </ModalTypeBtn>
                      </ModalTypeGrid>
                    </ModalField>
                    <ModalField>
                      <ModalLabel>But de la cible</ModalLabel>
                      <ModalTypeGrid>
                        <ModalTypeBtn $active={addForm.purpose === 'interact'} onClick={() => setAddForm(f => ({ ...f, purpose: 'interact' }))}>
                          <Heart size={18} /> Interaction
                        </ModalTypeBtn>
                        <ModalTypeBtn $active={addForm.purpose === 'scrape'} onClick={() => setAddForm(f => ({ ...f, purpose: 'scrape' }))}>
                          <Search size={18} /> Scraping
                        </ModalTypeBtn>
                      </ModalTypeGrid>
                    </ModalField>
                    <ModalField>
                      <ModalLabel>
                        {addForm.type === 'user' ? 'Nom d\'utilisateur Instagram' :
                         addForm.type === 'hashtag' ? 'Hashtag (sans #)' : 'URL ou identifiant du post'}
                      </ModalLabel>
                      <ModalInput
                        autoFocus
                        placeholder={addForm.type === 'user' ? 'ex: john_doe' : addForm.type === 'hashtag' ? 'ex: fitness' : 'ex: CxIJkL3mN...'}
                        value={addForm.value}
                        onChange={e => setAddForm(f => ({ ...f, value: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter' && addForm.value.trim()) handleAddTarget(); }}
                      />
                    </ModalField>
                  </ModalBody>
                  <ModalFooter>
                    <ModalCancelBtn onClick={() => { setShowAddModal(false); setAddForm({ value: '', type: 'user', purpose: 'interact' }); }}>
                      Annuler
                    </ModalCancelBtn>
                    <ModalSubmitBtn onClick={handleAddTarget} disabled={!addForm.value.trim() || addingTarget}>
                      <Plus size={16} /> {addingTarget ? 'Ajout...' : 'Ajouter'}
                    </ModalSubmitBtn>
                  </ModalFooter>
                </ModalBox>
              </ModalOverlay>
            )}
          </>
        );
      })()}

      {/* ════════ SESSIONS TAB ════════ */}
      {tab === 'sessions' && (
        <SessionsTabContent
          selAcct={selAcct}
          sessionsDate={sessionsDate}
          setSessionsDate={setSessionsDate}
          sessionsList={sessionsList}
          setSessionsList={setSessionsList}
          sessionsLoading={sessionsLoading}
          setSessionsLoading={setSessionsLoading}
          selectedSessionDetail={selectedSessionDetail}
          setSelectedSessionDetail={setSelectedSessionDetail}
          sessionActions={sessionActions}
          setSessionActions={setSessionActions}
          sessionActionsLoading={sessionActionsLoading}
          setSessionActionsLoading={setSessionActionsLoading}
        />
      )}

      {/* ════════ LISTES TAB ════════ */}
      {tab === 'listes' && (() => {
        const s = listSummary || {};
        const interact = s.interact || {};
        const unfollow = s.unfollow || {};
        const wl = s.whitelist || {};
        const bl = s.blacklist || {};

        // Filter interact entries
        let filteredInteract = interactEntries;
        if (interactSourceFilter) filteredInteract = filteredInteract.filter(e => e.source === interactSourceFilter);
        if (interactStatusFilter) filteredInteract = filteredInteract.filter(e => e.status === interactStatusFilter);
        if (listSearch) {
          const q = listSearch.toLowerCase();
          filteredInteract = filteredInteract.filter(e => e.target_username?.toLowerCase().includes(q) || e.source?.toLowerCase().includes(q));
        }

        // Filter unfollow entries
        let filteredUnfollow = unfollowEntries;
        if (listSearch) {
          const q = listSearch.toLowerCase();
          filteredUnfollow = filteredUnfollow.filter(e => e.target_username?.toLowerCase().includes(q));
        }

        // Filter whitelist / blacklist
        const filteredWl = listSearch ? whitelistEntries.filter(e => e.username?.toLowerCase().includes(listSearch.toLowerCase())) : whitelistEntries;
        const filteredBl = listSearch ? blacklistEntries.filter(e => e.username?.toLowerCase().includes(listSearch.toLowerCase())) : blacklistEntries;

        // Unique sources for filter dropdown
        const uniqueSources = [...new Set(interactEntries.map(e => e.source).filter(Boolean))];

        const statusBadge = (status) => {
          if (status === 'done') return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(34,197,94,.12)', color: '#22c55e' }}>✓ Done</span>;
          if (status === 'pending') return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(245,158,11,.12)', color: '#f59e0b' }}>⏳ Pending</span>;
          return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(239,68,68,.12)', color: '#ef4444' }}>⊘ {status}</span>;
        };

        const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';

        return (
          <>
            {/* Summary Cards */}
            <StatsRow>
              <StatCard $active={listSubTab === 'interact'} onClick={() => setListSubTab('interact')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(6,182,212,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap size={16} style={{ color: '#06b6d4' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.3px' }}>Interaction</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{interact.total || 0}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                  <span style={{ color: '#f59e0b' }}>⏳ {interact.pending || 0}</span>
                  <span style={{ color: '#22c55e' }}>✓ {interact.done || 0}</span>
                  <span style={{ color: '#ef4444' }}>⊘ {interact.skipped || 0}</span>
                </div>
              </StatCard>
              <StatCard $active={listSubTab === 'unfollow'} onClick={() => setListSubTab('unfollow')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(245,158,11,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserMinus size={16} style={{ color: '#f59e0b' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.3px' }}>Unfollow</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{unfollow.total || 0}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 10, color: 'var(--text-muted)' }}>
                  <span style={{ color: '#f59e0b' }}>⏳ {unfollow.pending || 0}</span>
                  <span style={{ color: '#22c55e' }}>✓ {unfollow.done || 0}</span>
                </div>
              </StatCard>
              <StatCard $active={listSubTab === 'whitelist'} onClick={() => setListSubTab('whitelist')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(34,197,94,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Check size={16} style={{ color: '#22c55e' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.3px' }}>Whitelist</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{wl.total || 0}</div>
                  </div>
                </div>
              </StatCard>
              <StatCard $active={listSubTab === 'blacklist'} onClick={() => setListSubTab('blacklist')} style={{ cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(239,68,68,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={16} style={{ color: '#ef4444' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.3px' }}>Blacklist</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{bl.total || 0}</div>
                  </div>
                </div>
              </StatCard>
            </StatsRow>

            {/* ── INTERACT sub-tab ── */}
            {listSubTab === 'interact' && (
              <>
                {/* Source breakdown */}
                {interactBySource.length > 0 && (
                  <DetailTable style={{ marginBottom: 16 }}>
                    <DetailTableHeader>
                      <span><Target size={14} /> Répartition par source ({interactBySource.length})</span>
                    </DetailTableHeader>
                    <div style={{ padding: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {interactBySource.map(src => (
                        <div
                          key={src.source}
                          onClick={() => setInteractSourceFilter(f => f === src.source ? '' : src.source)}
                          style={{
                            padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
                            background: interactSourceFilter === src.source ? 'rgba(6,182,212,.15)' : 'var(--surface)',
                            border: `1px solid ${interactSourceFilter === src.source ? '#06b6d4' : 'var(--border)'}`,
                            transition: 'all .15s'
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>@{src.source}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                            {src.profiles.length} profil{src.profiles.length > 1 ? 's' : ''} en attente
                          </div>
                        </div>
                      ))}
                    </div>
                  </DetailTable>
                )}

                {/* Filters + actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      value={listSearch} onChange={e => setListSearch(e.target.value)}
                      placeholder="Rechercher un profil..."
                      style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                    />
                  </div>
                  <select
                    value={interactSourceFilter} onChange={e => setInteractSourceFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                  >
                    <option value="">Toutes les sources</option>
                    {uniqueSources.map(s => <option key={s} value={s}>@{s}</option>)}
                  </select>
                  <select
                    value={interactStatusFilter} onChange={e => setInteractStatusFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="pending">En attente</option>
                    <option value="done">Terminé</option>
                    <option value="skipped">Ignoré</option>
                  </select>
                  <SmallBtn onClick={() => { loadInteractList(); loadListSummary(); }} disabled={interactLoading}>
                    <RefreshCw size={12} /> Actualiser
                  </SmallBtn>
                  {interactEntries.some(e => e.status === 'done') && (
                    <SmallBtn onClick={handleClearInteractDone} style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' }}>
                      <Trash2 size={12} /> Supprimer terminés
                    </SmallBtn>
                  )}
                </div>

                {/* Entries table */}
                <DetailTable>
                  <DetailTableHeader>
                    <span><List size={14} /> Liste d'interaction ({filteredInteract.length})</span>
                  </DetailTableHeader>
                  {interactLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                  ) : filteredInteract.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      <AlertCircle size={20} style={{ marginBottom: 8, opacity: .5 }} /><br />
                      Aucune entrée dans la liste d'interaction
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 140px 40px', padding: '8px 16px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                        <div>Profil</div>
                        <div>Source</div>
                        <div>Statut</div>
                        <div>Date</div>
                        <div></div>
                      </div>
                      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                        {filteredInteract.map(entry => (
                          <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 140px 40px', padding: '8px 16px', alignItems: 'center', borderBottom: '1px solid var(--border)', fontSize: 12, transition: 'background .1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <UserIcon size={12} style={{ color: 'var(--text-muted)' }} />
                              @{entry.target_username}
                            </div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                              {entry.source ? `@${entry.source}` : '-'}
                            </div>
                            <div>{statusBadge(entry.status)}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                              {entry.status === 'done' ? fmtDate(entry.processed_at) : fmtDate(entry.added_at)}
                            </div>
                            <div>
                              <button
                                onClick={() => handleDeleteInteractEntries([entry.target_username])}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}
                                title="Supprimer"
                              >
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </DetailTable>
              </>
            )}

            {/* ── UNFOLLOW sub-tab ── */}
            {listSubTab === 'unfollow' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      value={listSearch} onChange={e => setListSearch(e.target.value)}
                      placeholder="Rechercher..."
                      style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                    />
                  </div>
                  <SmallBtn onClick={() => { loadUnfollowList(); loadListSummary(); }} disabled={unfollowLoading}>
                    <RefreshCw size={12} /> Actualiser
                  </SmallBtn>
                  {unfollowEntries.some(e => e.status === 'done') && (
                    <SmallBtn onClick={handleClearUnfollowDone} style={{ color: '#ef4444', borderColor: 'rgba(239,68,68,.3)' }}>
                      <Trash2 size={12} /> Supprimer terminés
                    </SmallBtn>
                  )}
                </div>

                <DetailTable>
                  <DetailTableHeader>
                    <span><UserMinus size={14} /> Liste d'unfollow ({filteredUnfollow.length})</span>
                  </DetailTableHeader>
                  {unfollowLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                  ) : filteredUnfollow.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      <AlertCircle size={20} style={{ marginBottom: 8, opacity: .5 }} /><br />
                      Aucune entrée dans la liste d'unfollow
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 140px', padding: '8px 16px', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
                        <div>Profil</div>
                        <div>Source</div>
                        <div>Statut</div>
                        <div>Date</div>
                      </div>
                      <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                        {filteredUnfollow.map(entry => (
                          <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 100px 140px', padding: '8px 16px', alignItems: 'center', borderBottom: '1px solid var(--border)', fontSize: 12, transition: 'background .1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div style={{ fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <UserIcon size={12} style={{ color: 'var(--text-muted)' }} />
                              @{entry.target_username}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{entry.source || '-'}</div>
                            <div>{statusBadge(entry.status)}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace" }}>
                              {entry.status === 'done' ? fmtDate(entry.processed_at) : fmtDate(entry.added_at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </DetailTable>
              </>
            )}

            {/* ── WHITELIST sub-tab ── */}
            {listSubTab === 'whitelist' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      value={listSearch} onChange={e => setListSearch(e.target.value)}
                      placeholder="Rechercher..."
                      style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                    />
                  </div>
                  <SmallBtn onClick={loadFileLists} disabled={fileListLoading}>
                    <RefreshCw size={12} /> Actualiser
                  </SmallBtn>
                </div>
                <DetailTable>
                  <DetailTableHeader>
                    <span><Check size={14} /> Whitelist ({filteredWl.length})</span>
                  </DetailTableHeader>
                  {fileListLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                  ) : filteredWl.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      <AlertCircle size={20} style={{ marginBottom: 8, opacity: .5 }} /><br />
                      Whitelist vide
                    </div>
                  ) : (
                    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                      {filteredWl.map((entry, i) => (
                        <div key={i} style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, transition: 'background .1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <Check size={12} style={{ color: '#22c55e', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.username || entry.value || entry}</span>
                          {entry.file && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>{entry.file}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </DetailTable>
              </>
            )}

            {/* ── BLACKLIST sub-tab ── */}
            {listSubTab === 'blacklist' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      value={listSearch} onChange={e => setListSearch(e.target.value)}
                      placeholder="Rechercher..."
                      style={{ width: '100%', padding: '8px 12px 8px 32px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 12 }}
                    />
                  </div>
                  <SmallBtn onClick={loadFileLists} disabled={fileListLoading}>
                    <RefreshCw size={12} /> Actualiser
                  </SmallBtn>
                </div>
                <DetailTable>
                  <DetailTableHeader>
                    <span><X size={14} /> Blacklist ({filteredBl.length})</span>
                  </DetailTableHeader>
                  {fileListLoading ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Chargement...</div>
                  ) : filteredBl.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      <AlertCircle size={20} style={{ marginBottom: 8, opacity: .5 }} /><br />
                      Blacklist vide
                    </div>
                  ) : (
                    <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                      {filteredBl.map((entry, i) => (
                        <div key={i} style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, transition: 'background .1s' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <X size={12} style={{ color: '#ef4444', flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{entry.username || entry.value || entry}</span>
                          {entry.file && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>{entry.file}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </DetailTable>
              </>
            )}
          </>
        );
      })()}

      {/* Graphiques tab is now its own dedicated tab */}
    </Page>
  );
};

export default StatisticsNew;
